/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import BloomFilter from "../utils/bloomfilter/BloomFilter";
import { tqconstants } from "../utils/constants/TQConstants";
import TQFont, { tqtypes, TQFontAggregation, TQFontFamily, TQLogLevel } from "../public/TQTypes";
import TQInitParams from "../public/TQInitParam";
import { TQHttpConnector, TQHttpRequest, TQHttpResponse } from "../network/TQServiceWorker";
import { TQIdb, BrowseTables, PreferenceTables } from "../utils/cache/TQIdb";
import { TQUtils, HashAlgo } from "../utils/TQUtils";
import { TaskTimer } from "../utils/timer/TaskTimer";
import TQResponseStorageCache, { CacheResponseType } from "../utils/cache/TQResponseStorageCache";
import { DiskCache } from "../utils/cache/TQDiskCache";
import { DOC_FOLDER_EMFS, FileSystem } from "../utils/filesystem/TQFile";
import { TQFontParser } from "../utils/fontparser/TQFontParser";
import { TQByofFileValidatorResult } from "../public/TQByofFileValidator";
import { TQCustomCatalogHandler } from "./TQCustomCatalogHandler";
import TQFoundFontHandler from "../utils/foundfonthandler/TQFoundFontHandler";

export const recentFontsMaxCount = 10;
export const catalogStatusCacheEntry = "tempCache";

const selectionsPollTaskId = "selectionsPoll";
const byofPollTaskId = "byofPoll";
const fontsUpdateNotifyTaskId = "fontsUpdateNotify";
export const downloaderVersion = "_1";

enum CatalogStatus {
    UNINITIALIZED = 0,
    IN_PROGRESS = 1,
    LOCAL_DONE = 2,
    DONE = 3,
}

export default class TQTaskScheduler {
    private _status: tqtypes.StatusCode = tqtypes.StatusCode.UNINITIALIZED;
    private _catalogStatus: CatalogStatus = CatalogStatus.UNINITIALIZED;
    private _tagCatalogLastCheck = 0;
    private _byofFontsLastCheck = 0;
    private _initParams: TQInitParams;
    private _obtainedTokenParams: tqtypes.ObtainedTokenParams = new tqtypes.ObtainedTokenParams();
    private _channel: BroadcastChannel | undefined;
    private _postCatalogTaskQueue: any = [];
    private _dbWrapper: TQIdb = new TQIdb();
    private _recommendedUrlsMap = new Map<tqtypes.FontType, string>();
    private _timer = new TaskTimer(1000, 20000); // Every 1 sec, starts after 20 secs
    private _foundFontsHandler: TQFoundFontHandler;

    // Selections and BYOF in memory structures
    private _selections: Set<string> = new Set<string>();
    private _byofFonts: Set<string> = new Set<string>();
    private _responseCache: TQResponseStorageCache = new TQResponseStorageCache();
    private _selectionsEtag = "";
    private _byofsEtag = "";
    private _allByofFontHashes = new Map<string, TQFont>();
    private _cachedFonts = new Map<string, TQFont>();
    private _collabFonts = new Map<string, TQFont>();

    // Others
    private _accessTokenByofStatus = new Map<string, boolean>([["", false]]);
    public _nonBlockingTokenCallStatus = false;
    private _accessTokenBlockedSet = new Set<string>();

    private static _cachedFontsUrlProd = new Map<string, string>();
    private static _cachedFontsUrlStage = new Map<string, string>();
    public static _schedulersList: TQTaskScheduler[] = [];

    static kMaxDiskCacheSize = 1000 * 1000 * 1000; // ~1000MB
    static kMaxNumFonts = 2000;
    public static _diskCache: DiskCache;

    public get status(): tqtypes.StatusCode {
        return this._status;
    }
    public set status(value: tqtypes.StatusCode) {
        this._status = value;
    }

    public get channel(): BroadcastChannel | undefined {
        return this._channel;
    }

    public get foundFontsHandler(): TQFoundFontHandler {
        return this._foundFontsHandler;
    }
    public set foundFontsHandler(value: TQFoundFontHandler) {
        this._foundFontsHandler = value;
    }

    public get responseCache(): TQResponseStorageCache {
        return this._responseCache;
    }
    public set responseCache(value: TQResponseStorageCache) {
        this._responseCache = value;
    }

    public cachedFonts(fontType: tqtypes.FontType = tqtypes.FontType.DEFAULT) {
        if (fontType == tqtypes.FontType.DEFAULT) {
            return this._cachedFonts;
        } else {
            return new Map([...this._cachedFonts].filter(([key, font]) => font.type == fontType));
        }
    }

    public get collabFonts() {
        return this._collabFonts;
    }
    public set collabFonts(value) {
        this._collabFonts = value;
    }

    public get catalogStatus(): CatalogStatus {
        return this._catalogStatus;
    }
    public set catalogStatus(value: CatalogStatus) {
        this._catalogStatus = value;
    }

    public get obtainedTokenParams(): tqtypes.ObtainedTokenParams {
        return this._obtainedTokenParams;
    }
    public set obtainedTokenParams(value: tqtypes.ObtainedTokenParams) {
        this._obtainedTokenParams = value;
    }

    public get dbWrapper(): TQIdb {
        return this._dbWrapper;
    }
    public set dbWrapper(value: TQIdb) {
        this._dbWrapper = value;
    }

    public static async updateCachedSelectionsAndByof(updates: tqtypes.FontUpdateNotification[], schedulerList: TQTaskScheduler[]) {
        if (schedulerList.length == 0) {
            schedulerList = TQTaskScheduler._schedulersList;
        }
        for (const scheduler of schedulerList) {
            for (const update of updates) {
                if (update.notificationType == tqtypes.FontUpdateNotificationType.SELECTIONS) {
                    if (update.operationType == tqtypes.FontUpdateOperationType.ADDED) {
                        scheduler._selections.add(update.font.postscriptName); // ADD
                        scheduler._cachedFonts.set(update.font.postscriptName, update.font);
                    } else if (update.operationType == tqtypes.FontUpdateOperationType.REMOVED) {
                        scheduler._selections.delete(update.font.postscriptName); // REMOVE
                        scheduler._cachedFonts.delete(update.font.postscriptName);
                    }
                } else if (update.notificationType == tqtypes.FontUpdateNotificationType.BYOF) {
                    if (update.operationType == tqtypes.FontUpdateOperationType.ADDED) {
                        scheduler._byofFonts.add(update.font.postscriptName); // ADD
                        scheduler._cachedFonts.set(update.font.postscriptName, update.font);
                    } else if (update.operationType == tqtypes.FontUpdateOperationType.REMOVED) {
                        scheduler._byofFonts.delete(update.font.postscriptName); // REMOVE
                        scheduler._cachedFonts.delete(update.font.postscriptName);
                    }
                }
            }
        }
    }

    public async publishMessage(fontsData: any[]) {
        const fonts: TQFont[] = [];
        for (const value of fontsData) {
            try {
                fonts.push(TQFont.fromJSON(value.fontJson));
            } catch (e) {
                // ..
            }
        }
        this.initParams.recentFontsUpdateCallback(fonts);
    }

    public startTimers() {
        const fontsUpdateNotifyCallback = async () => {
            this.fireFontUpdates();
        };

        const isAccessTokenValid = TQUtils.isValidAccessToken(this.initParams.authParams.accessToken);
        const selectionsPollTaskcallback = async () => {
            const callback: tqtypes.GenericContext<tqtypes.AggregationContext> = {
                root: {
                    page: 1,
                    perPage: 1,
                    isRequested: false,
                },
                retryCount: 1,
            };
            if (isAccessTokenValid && this.initParams.fontsUpdateCallback && this._selections.size > 0 && this._status == tqtypes.StatusCode.DONE) {
                this.scheduleTask(tqtypes.TaskType.ACTIVATED_FONTS_POLL, callback);
            }
        };
        const byofPollTaskcallback = async () => {
            if (this._obtainedTokenParams.entitlement == tqtypes.Entitlement.TRIAL || this.initParams.watchFontUpdates.indexOf(tqtypes.FontType.BYOF) == -1) {
                return;
            }

            const callback: tqtypes.GenericContext<tqtypes.AggregationContext> = {
                root: {
                    page: 1,
                    perPage: 1,
                    isRequested: false,
                },
                retryCount: 1,
            };
            if (isAccessTokenValid && this.initParams.fontsUpdateCallback && this._status == tqtypes.StatusCode.DONE) {
                this.scheduleTask(tqtypes.TaskType.BYOF_FONTS_POLL, callback);
            }
        };
        let isTaskAdded = false;
        if (isAccessTokenValid && this.initParams.runtimeMode != tqtypes.RuntimeMode.FOUND_FONTS && this._timer.getTask(selectionsPollTaskId).id == "") {
            // ..
            isTaskAdded = true;
            this._timer.addTask(selectionsPollTaskId, selectionsPollTaskcallback, this.initParams.selectionPollInterval, false);
        }

        if (isAccessTokenValid && TQUtils.isGivenScopePresent(this._initParams.authParams.accessToken, "af_byof") && this._timer.getTask(byofPollTaskId).id == "") {
            isTaskAdded = true;
            this._timer.addTask(byofPollTaskId, byofPollTaskcallback, this.initParams.selectionPollInterval, true);
        }

        if (isTaskAdded && this._timer.getTask(fontsUpdateNotifyTaskId).id == "") {
            this._timer.addTask(fontsUpdateNotifyTaskId, fontsUpdateNotifyCallback, 12, false);
        }
    }

    public async togglePollTimers(pauseNeeded: boolean) {
        if (pauseNeeded) {
            this._timer.pause();
        } else if (this._timer.isPaused()) {
            this._timer.resume();
        }
    }

    public performByofValidationScope() {
        const accessToken = this._initParams.authParams.accessToken;
        if (TQUtils.isValidAccessToken(accessToken) && TQUtils.isGivenScopePresent(accessToken, "af_byof")) {
            // scopes are present
            return true;
        }
        return false;
    }

    private addDemoCollectionToCachedFonts() {
        const emojiFont = new TQFont(tqtypes.FontType.DEFAULT, "EmojiOneColor", "f612971c-aec5-447d-9a09-4428dd770ec9");
        emojiFont.familyName = "EmojiOne";
        emojiFont.styleName = "Color";
        emojiFont.fontName = emojiFont.familyName + " " + emojiFont.styleName;
        emojiFont.script = tqtypes.Language.Emoji;
        emojiFont.isActiveForUser = true;
        emojiFont.addSupportedLanguage(tqtypes.Language.Emoji);
        this._cachedFonts.set("EmojiOneColor", emojiFont);
        this._cachedFonts.set(emojiFont.fontId, emojiFont);
        TQTaskScheduler._cachedFontsUrlProd.set(
            "EmojiOneColor",
            "https://faster.typekit.net/try/library/rocket-full/otf/f612971c-aec5-447d-9a09-4428dd770ec9" +
                "?__token__=exp=1674353427~acl=/try/library/rocket-full/otf/*~hmac=1723b20f81e1909a975ecf18937a7f390c0c4f56344a288910543695195cc0e7"
        );
        TQTaskScheduler._cachedFontsUrlStage.set(
            "EmojiOneColor",
            "https://faster-staging.typekit.net/try/library/rocket-full/otf/f612971c-aec5-447d-9a09-4428dd770ec9" +
                "?__token__=exp=1674353168~acl=/try/library/rocket-full/otf/*~hmac=e95cffad0784845c76a3d2892795edfdc623ab99a5692bd1cf735bf41a3b7d9d"
        );
    }

    constructor(init: TQInitParams) {
        this._initParams = init;
        this._foundFontsHandler = new TQFoundFontHandler(this);

        try {
            this._channel = new BroadcastChannel("Recents_" + this.initParams.authParams.appId + "_" + this.initParams.authParams.userId);
            this._channel.onmessage = event => {
                try {
                    const receivedRecents: string[] = event.data as any[];
                    this.publishMessage(receivedRecents);
                } catch (e) {
                    // ...
                }
            };
            this._responseCache.init();
            if (this.initParams.watchFontUpdates.length || this.initParams.runtimeMode == tqtypes.RuntimeMode.ALL) {
                this.startTimers();
            }
            if (this.initParams.runtimeMode == tqtypes.RuntimeMode.FOUND_FONTS) {
                this.addDemoCollectionToCachedFonts();
            }
        } catch (e) {
            // ...
        }

        {
            if (this._initParams.diskCache == tqtypes.DiskCacheType.OPFS) {
                FileSystem.getAvailableStorage().then(storageAvailable => {
                    if (!FileSystem.isEstimateEnabled() || storageAvailable >= TQTaskScheduler.kMaxDiskCacheSize) {
                        TQTaskScheduler._diskCache = new DiskCache(TQTaskScheduler.kMaxDiskCacheSize, DOC_FOLDER_EMFS, TQTaskScheduler.kMaxNumFonts);
                    } else {
                        TQTaskScheduler._diskCache = new DiskCache(storageAvailable, DOC_FOLDER_EMFS, TQTaskScheduler.kMaxNumFonts);
                    }
                });
            }
        }

        TQTaskScheduler._schedulersList.push(this);
    }

    public get initParams(): TQInitParams {
        return this._initParams;
    }
    public set initParams(value: TQInitParams) {
        this._initParams = value;
    }

    private async handleAuthFailure() {
        this._status = tqtypes.StatusCode.ERROR_OCCURED;
        this._initParams.statusUpdateCallback(this._status, new tqtypes.Error(tqtypes.ErrorCode.AUTH_FAILURE, "Auth Token Error", true));
    }

    public async populateByofScript(font: TQFont) {
        const fontScriptKey = CacheResponseType.BYOF_SCRIPT_ENDPOINT_PREFIX + font.fontId + downloaderVersion + CacheResponseType.BYOF_SCRIPT_ENDPOINT_SUFFIX;
        const byofScriptJson = await this._responseCache.getValue(fontScriptKey);
        if (font.fontId != "" && byofScriptJson) {
            font.script = byofScriptJson;
        } else {
            if (font.fontBuffer.length) {
                const fontParser = new TQFontParser(font.fontBuffer);
                font.script = fontParser.computeFontScript(font.postscriptName);
                this._responseCache.putValue(fontScriptKey, font.script);
            }
        }
    }

    public async hasUserAccessForByofUpload(): Promise<{ error: tqtypes.Error; hasAccess: boolean }> {
        // Check in access token map
        const err = new tqtypes.Error();
        let hasAccess = this._accessTokenByofStatus.get(this._initParams.authParams.accessToken);
        if (hasAccess != undefined) {
            if (TQUtils.isValidAccessToken(this._initParams.authParams.accessToken) == false) {
                err.errCode = tqtypes.ErrorCode.AUTH_FAILURE;
            }
            return { error: err, hasAccess: hasAccess };
        }
        if (this.performByofValidationScope() == false) {
            hasAccess = false;
            return { error: err, hasAccess: hasAccess };
        }
        // Now check in cache
        const cachedUserAccessLevelKey = CacheResponseType.BYOF_ACCESS_ENDPOINT_PREFIX + this._initParams.authParams.userId + CacheResponseType.BYOF_ACCESS_ENDPOINT_SUFFIX;
        hasAccess = await this._responseCache.getValue(cachedUserAccessLevelKey);
        if (hasAccess == undefined) {
            // Neither present in access token map nor in cache
            return await this.scheduleTask(tqtypes.TaskType.BYOF_ACCESS_LEVEL, null);
        } else {
            // Found in cache, hence update in background to fill the access token map
            setTimeout(() => {
                this.scheduleTask(tqtypes.TaskType.BYOF_ACCESS_LEVEL, null);
            }, 30000);
        }
        if (hasAccess == undefined) {
            // Still undefined for unknown reason??
            hasAccess = true;
        }
        return { error: err, hasAccess: hasAccess };
    }

    public async deleteByofAuth(checksums: string[]): Promise<{ error: tqtypes.Error; status: boolean }> {
        if (checksums.length > 0) {
            const callbacks: tqtypes.GenericContext<tqtypes.InternalContext> = {
                root: {
                    downloadCB: null as any,
                    fontsList: checksums,
                    fontsIds: [],
                    unicodesList: [],
                    fontInfo: new TQFont(),
                },
                retryCount: 1,
            };
            return await this.scheduleTask(tqtypes.TaskType.BYOF_DELETE, callbacks);
        }
        return { error: new tqtypes.Error(), status: false };
    }

    public populateCharacterSet(font: TQFont) {
        if (this.initParams.runtimeMode != tqtypes.RuntimeMode.FOUND_FONTS && font.fontBuffer.length) {
            try {
                const parser = new TQFontParser(font.fontBuffer);
                font.characterSet = parser.charSet;
            } catch (e) {
                // ...
            }
        }
    }

    public async fetchCachedEntitlement() {
        const isAccessTokenValid = TQUtils.isValidAccessToken(this.initParams.authParams.accessToken);
        if (TQInitParams.cachedEntitlement != undefined) {
            return TQInitParams.cachedEntitlement;
        }
        if (this.initParams.authParams.userId != "" && isAccessTokenValid == false) {
            TQInitParams.cachedEntitlement = tqtypes.Entitlement.ANONYMOUS;
            return tqtypes.Entitlement.ANONYMOUS;
        }
        if (this.initParams.authParams.userId != "" && isAccessTokenValid) {
            const fontKey = CacheResponseType.ENTITLEMENT_ENDPOINT_PREFIX + encodeURIComponent(this.initParams.authParams.userId) + CacheResponseType.ENTITLEMENT_ENDPOINT_SUFFIX;
            const ret = await this.responseCache.getValue(fontKey);
            if (ret == tqtypes.Entitlement.FULL || ret == tqtypes.Entitlement.TRIAL) {
                TQInitParams.cachedEntitlement = ret;
                return ret;
            }
        }
        return undefined;
    }

    public async getFontFromDisk(fileName: string) {
        if (this.initParams.diskCache == tqtypes.DiskCacheType.OPFS && TQTaskScheduler._diskCache.getFile(fileName) != "") {
            const fontStream = await FileSystem.ReadFile(DOC_FOLDER_EMFS, fileName);
            return new Uint8Array(fontStream);
        }
    }

    public async getDemoFontsCollection() {
        this.addDemoCollectionToCachedFonts();
        const fonts: TQFont[] = [];
        for (const [psName, url] of TQTaskScheduler._cachedFontsUrlProd) {
            const font = this._cachedFonts.get(psName);
            if (font) {
                fonts.push(font);
            }
        }
        const aggregation = TQUtils.getAggregationFromFonts(fonts);
        return { aggregation: aggregation, error: new tqtypes.Error() };
    }

    public static checkByofDuplicacy(sha256sum: string) {
        for (const scheduler of TQTaskScheduler._schedulersList) {
            if (scheduler._allByofFontHashes.has(sha256sum) == true) {
                return true;
            }
        }
        return false;
    }

    public async prevalidateByofFont(file: File): Promise<TQByofFileValidatorResult> {
        TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, file);
        const fontNameSanitizerProc = async (fontPsName: string) => {
            return await this.getFontFromLocalInfo(fontPsName);
        };
        const byofValidator = new TQByofFileValidatorResult(file, fontNameSanitizerProc);
        await byofValidator._validateInternal(false); // deep read
        return byofValidator;
    }

    public async uploadByofFont(file: File) {
        TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, file);
        const byofValidator = new TQByofFileValidatorResult(file);
        if (false == (await byofValidator._validateInternal(true))) {
            // Map and return errors, shallow read
            return { status: false, error: new tqtypes.Error(tqtypes.ErrorCode.UNKNOWN, byofValidator.byofError, true) };
        }
        const callback: tqtypes.GenericContext<tqtypes.UploadContext> = {
            root: {
                file: file,
            },
            retryCount: 1,
        };
        return await this.scheduleTask(tqtypes.TaskType.BYOF_UPLOAD_URL, callback);
    }

    public async updateRecentsListAndBroadcastMessage(fonts: TQFont[]) {
        const maxCount = 10;
        if (fonts.length) {
            const recentFontsMap = new Map<TQFont, number>();
            const values = await this.dbWrapper.getRecentListRaw(recentFontsMaxCount);
            for (const value of values) {
                try {
                    const font = TQFont.fromJSON(value.fontJson);
                    recentFontsMap.set(font, value.timestamp);
                } catch (e) {
                    // ...
                }
            }
            for (const font of fonts) {
                recentFontsMap.set(font, new Date().getTime());
            }
            let dbValues = [...recentFontsMap.entries()].sort((a, b) => b[1] - a[1]); // Sort in descending time
            dbValues = dbValues.slice(0, 10);
            // Clear table
            const recentFontsData: any[] = [];
            for (const value of dbValues) {
                recentFontsData.push({
                    psName: value[0].postscriptName,
                    fontJson: value[0].toJSON(),
                    timestamp: value[1],
                });
            }
            await this.dbWrapper.bulkPutPrefsDbData(PreferenceTables.RECENTS, recentFontsData);
        }
        this.getRecentListSortedAndBroadcastMessage();
    }

    public async getRecentListSortedAndBroadcastMessage(broadcast = true) {
        const values = await this.dbWrapper.getRecentListSorted(recentFontsMaxCount);
        const recentsList: TQFont[] = [];
        for (const value of values) {
            try {
                recentsList.push(TQFont.fromJSON(value.fontJson));
            } catch (e) {
                // ..
            }
        }
        if (this.channel && broadcast) {
            this.channel.postMessage(values);
        }
        this.initParams.recentFontsUpdateCallback(recentsList);
    }

    private _processUseFontReportResponse: (response: TQHttpResponse, _callback: any) => boolean = (response: TQHttpResponse, _callback: any) => {
        if (response.status != tqconstants.ResponseStatus.OK) {
            return false;
        } else {
            return true;
        }
    };

    public async getCachedActivated(etag: string, fontAggregation: TQFontAggregation, isRequested: boolean) {
        const psNames: string[] = [];
        // Check if what we delivered last time and send notifications for difference
        const cacheLayerFn = (key: string) => {
            return etag == "" ? this._responseCache.getInMemoryOrFromResponseCache(key) : this._responseCache.getValue(key);
        };
        const activatedRes = await cacheLayerFn(
            CacheResponseType.SELECTIONS_LIST_ENDPOINT_PREFIX + this._initParams.getLanguage() + "_" + this.initParams.authParams.userId + CacheResponseType.SELECTIONS_LIST_ENDPOINT_SUFFIX
        );
        const cachedActivatdEtag = await cacheLayerFn(CacheResponseType.SELECTIONS_ETAG_ENDPOINT_PREFIX + this.initParams.authParams.userId + CacheResponseType.SELECTIONS_ETAG_ENDPOINT_SUFFIX);

        if ((etag == "" || cachedActivatdEtag == etag) && activatedRes) {
            TQUtils.getFamiliesFromFontsArrayJSON(activatedRes, tqtypes.FontType.ACTIVATED, this.obtainedTokenParams.entitlement, fontAggregation, "", null, this.initParams.appEntitlement, true, []);
            fontAggregation = TQUtils.setDisplayFontForEachFamilyIfNeeded(fontAggregation);
            for (const entry of fontAggregation.getEntries()) {
                for (const font of entry[1].getEntries()) {
                    psNames.push(font.postscriptName);
                    this._cachedFonts.set(font.postscriptName, font);
                }
            }
            if (isRequested) {
                // Cache in-memory psNames and ETag if it was requested
                this._selections = new Set(psNames);
                if (etag != "") {
                    this._selectionsEtag = etag;
                } else {
                    this._selectionsEtag = cachedActivatdEtag;
                }
            }
            return true;
        }
        return false;
    }

    private async _activatedCountResponse(response: TQHttpResponse, _callback: any) {
        const res = {
            aggregation: new TQFontAggregation(),
            error: new tqtypes.Error(),
        };
        const countContext = _callback as tqtypes.GenericContext<tqtypes.AggregationContext>;
        let fontAggregation: TQFontAggregation = new TQFontAggregation();

        let etag: string = this.getResponseHeader(response, "etag");
        etag = TQUtils.parseETagValue(etag);

        if (TQUtils.mapStatusNumberToErrorCode(response.status) != tqtypes.ErrorCode.NONE) {
            // ...
            if (response.status == tqconstants.ResponseStatus.UNAUTHORIZED) {
                countContext.retryCount = 0;
            } else {
                countContext.retryCount = countContext.retryCount - 1;
            }

            if (countContext.retryCount > 0) {
                const ret = (await this.scheduleTask(tqtypes.TaskType.ACTIVATED_FONTS_COUNT, countContext)) as Promise<typeof res>;
                return ret;
            } else {
                const errCode = TQUtils.mapStatusNumberToErrorCode(response.status);
                const err = new tqtypes.Error(errCode, "Activated fonts Error", false);
                if (err.errCode == tqtypes.ErrorCode.AUTH_FAILURE) {
                    this.handleAuthFailure();
                }
                return { aggregation: fontAggregation, error: err };
            }
        } else {
            let totalCount = 0;
            response.headers.forEach(entry => {
                if (entry[0] == "total-count") {
                    totalCount = +entry[1];
                    countContext.root.page = Math.floor(totalCount / 100) + 1;
                    countContext.root.perPage = 100;
                }
            });

            // Check if what we delivered last time and send notifications for difference
            const activatedRes = await this._responseCache.getValue(
                CacheResponseType.SELECTIONS_LIST_ENDPOINT_PREFIX + this._initParams.getLanguage() + "_" + this.initParams.authParams.userId + CacheResponseType.SELECTIONS_LIST_ENDPOINT_SUFFIX
            );
            const cachedActivatdEtag = await this._responseCache.getValue(
                CacheResponseType.SELECTIONS_ETAG_ENDPOINT_PREFIX + this.initParams.authParams.userId + CacheResponseType.SELECTIONS_ETAG_ENDPOINT_SUFFIX
            );

            const cachedValid = await this.getCachedActivated(etag, fontAggregation, countContext.root.isRequested);

            if (cachedValid) {
                const err = new tqtypes.Error();
                return { aggregation: fontAggregation, error: err };
            } else {
                // Schedule all calls one by one

                let allActivtedFontsJson: any[] = [];
                const promises: any = [];
                const res = response.body;
                const pages = countContext.root.page;
                for (let pageIndex = 1; pageIndex < countContext.root.page; pageIndex++) {
                    const contextCopy: tqtypes.GenericContext<tqtypes.AggregationContext> = {
                        root: {
                            page: pageIndex + 1,
                            perPage: countContext.root.perPage,
                            isRequested: countContext.root.isRequested,
                        },
                        retryCount: countContext.retryCount,
                    };
                    promises.push(this.scheduleTask(tqtypes.TaskType.ACTIVATED_FONTS, contextCopy));
                }
                const results: any[] = [{ res: response.body, etag: etag }];
                if (promises.length) {
                    results.push(...(await Promise.all(promises)));
                }

                results.forEach((entry: any) => {
                    const fontInfos = entry.res;
                    if (fontInfos == "") {
                        return;
                    }
                    allActivtedFontsJson = [...allActivtedFontsJson, ...fontInfos];
                    TQUtils.getFamiliesFromFontsArrayJSON(
                        fontInfos,
                        tqtypes.FontType.ACTIVATED,
                        this.obtainedTokenParams.entitlement,
                        fontAggregation,
                        "",
                        null,
                        this.initParams.appEntitlement,
                        true,
                        []
                    );
                });

                fontAggregation = TQUtils.setDisplayFontForEachFamilyIfNeeded(fontAggregation);
                const psNames: string[] = [];
                for (const entry of fontAggregation.getEntries()) {
                    for (const font of entry[1].getEntries()) {
                        psNames.push(font.postscriptName);
                        this._cachedFonts.set(font.postscriptName, font);
                    }
                }

                // Just cache them silently
                this._responseCache.putValue(
                    CacheResponseType.SELECTIONS_LIST_ENDPOINT_PREFIX + this._initParams.getLanguage() + "_" + this.initParams.authParams.userId + CacheResponseType.SELECTIONS_LIST_ENDPOINT_SUFFIX,
                    allActivtedFontsJson
                );
                this._responseCache.putValue(CacheResponseType.SELECTIONS_ETAG_ENDPOINT_PREFIX + this.initParams.authParams.userId + CacheResponseType.SELECTIONS_ETAG_ENDPOINT_SUFFIX, etag);
                this.dbWrapper.putSelectionsData(etag, psNames);
                const incomingSelectionsSet = new Set(psNames);

                if (countContext.root.isRequested) {
                    // Cache in-memory psNames and ETag if it was requested
                    this._selections = incomingSelectionsSet;
                    this._selectionsEtag = etag;
                } else {
                    // ...
                    setTimeout(() => {
                        this.fireFontUpdates();
                    }, 1000);
                }

                const err = new tqtypes.Error();
                return { aggregation: fontAggregation, error: err };
            }
        }
    }

    private async fireActivatedUpdates() {
        // Check if what we delivered last time and send notifications for difference
        const dbData = await this.dbWrapper.getSelectionsData();
        if (dbData && dbData.psNames.length && this._selectionsEtag != "" && dbData.etag != this._selectionsEtag) {
            // Etag mismatch
            const incomingSelectionsSet = new Set(dbData.psNames);
            this._selectionsEtag = dbData.etag;

            const updates: tqtypes.InternalFontUpdateNotification[] = [];
            for (const psName of this._selections) {
                if (false == incomingSelectionsSet.has(psName)) {
                    // To be removed
                    updates.push({
                        fontInfo: psName,
                        isVariationId: false,
                        updateType: tqtypes.FontUpdateOperationType.REMOVED,
                        contextData: null,
                    });
                } else {
                    incomingSelectionsSet.delete(psName);
                }
            }

            // Now incoming selections are only those which needs addition
            for (const psName of incomingSelectionsSet) {
                // To be added
                updates.push({
                    fontInfo: psName as string,
                    isVariationId: false,
                    updateType: tqtypes.FontUpdateOperationType.ADDED,
                    contextData: null,
                });
            }

            if (updates.length) {
                const finalUpdates: tqtypes.FontUpdateNotification[] = [];
                for (const entry of updates) {
                    if (false == entry.isVariationId) {
                        const font = await this.getFontFromLocalOrRemoteInfo(entry.fontInfo, "", tqtypes.FontType.ACTIVATED); // Only local check, no server
                        if (font) {
                            finalUpdates.push(new tqtypes.FontUpdateNotification(font, entry.updateType, tqtypes.FontUpdateNotificationType.SELECTIONS));
                        }
                    } else {
                        const fontsMap = await this.dbWrapper.getFontDataFromVariationId([entry.fontInfo], tqtypes.FontType.ACTIVATED);
                        if (fontsMap.size > 0) {
                            finalUpdates.push(new tqtypes.FontUpdateNotification(fontsMap.entries().next().value, entry.updateType, tqtypes.FontUpdateNotificationType.SELECTIONS));
                        }
                    }
                }
                for (const scheduler of TQTaskScheduler._schedulersList) {
                    if (scheduler.initParams.fontsUpdateCallback) {
                        scheduler.initParams.fontsUpdateCallback(finalUpdates);
                    } else {
                        // No callback defined, Check if found fonts mode. If yes, update our cached selections
                        TQTaskScheduler.updateCachedSelectionsAndByof(finalUpdates, [this]);
                    }
                }
            }
        }
    }

    private async fireByofUpdates() {
        // Check if what we delivered last time and send notifications for difference
        const byofRes = await this._responseCache.getValue(CacheResponseType.BYOF_LIST_ENDPOINT_PREFIX + this.initParams.authParams.userId + CacheResponseType.BYOF_LIST_ENDPOINT_SUFFIX);
        const byofEtag = await this._responseCache.getValue(CacheResponseType.BYOF_ETAG_ENDPOINT_PREFIX + this.initParams.authParams.userId + CacheResponseType.BYOF_ETAG_ENDPOINT_SUFFIX);

        if (byofRes && this._byofsEtag != "" && this._byofsEtag != byofEtag) {
            // Etag mismatch
            const incomingByofMap = new Map<string, TQFont>();
            this._byofsEtag = byofEtag;
            const fonts = TQUtils.getFontsFromByofData(byofRes);
            for (const font of fonts) {
                incomingByofMap.set(font.postscriptName, font);
            }

            const updates: tqtypes.InternalFontUpdateNotification[] = [];
            for (const psName of this._byofFonts) {
                if (false == incomingByofMap.has(psName)) {
                    // To be removed
                    let fontIfExists = this._cachedFonts.get(psName);
                    if (fontIfExists == undefined) {
                        fontIfExists = new TQFont(tqtypes.FontType.BYOF, psName, "byof-font");
                    }
                    updates.push({
                        fontInfo: psName,
                        isVariationId: false,
                        updateType: tqtypes.FontUpdateOperationType.REMOVED,
                        contextData: fontIfExists,
                    });
                } else {
                    incomingByofMap.delete(psName);
                }
            }

            // Now incoming selections are only those which needs addition
            for (const [psName, font] of incomingByofMap) {
                // To be added
                updates.push({
                    fontInfo: psName as string,
                    isVariationId: false,
                    updateType: tqtypes.FontUpdateOperationType.ADDED,
                    contextData: font,
                });
            }

            if (updates.length) {
                const finalUpdates: tqtypes.FontUpdateNotification[] = [];
                for (const entry of updates) {
                    if (entry.contextData) {
                        try {
                            const font = entry.contextData as TQFont;
                            finalUpdates.push(new tqtypes.FontUpdateNotification(font, entry.updateType, tqtypes.FontUpdateNotificationType.BYOF));
                        } catch (e) {
                            // ..
                        }
                    } else if (false == entry.isVariationId) {
                        const font = await this.getFontFromLocalOrRemoteInfo(entry.fontInfo, "", tqtypes.FontType.BYOF); // Only local check, no server
                        if (font) {
                            finalUpdates.push(new tqtypes.FontUpdateNotification(font, entry.updateType, tqtypes.FontUpdateNotificationType.BYOF));
                        }
                    } else {
                        const fontsMap = await this.dbWrapper.getFontDataFromVariationId([entry.fontInfo], tqtypes.FontType.BYOF);
                        if (fontsMap.size > 0) {
                            finalUpdates.push(new tqtypes.FontUpdateNotification(fontsMap.entries().next().value, entry.updateType, tqtypes.FontUpdateNotificationType.BYOF));
                        }
                    }
                }
                for (const scheduler of TQTaskScheduler._schedulersList) {
                    if (scheduler.initParams.fontsUpdateCallback) {
                        scheduler.initParams.fontsUpdateCallback(finalUpdates);
                    } else {
                        // No callback defined, Check if found fonts mode. If yes, update our cached selections
                        TQTaskScheduler.updateCachedSelectionsAndByof(finalUpdates, [this]);
                    }
                }
            }
        }
    }

    public async fireFontUpdates() {
        await this.fireActivatedUpdates();
        await this.fireByofUpdates();
    }

    public async queryBrowseDB(searchItems: string[], searchKeys: string[], tableName = BrowseTables.BROWSE, exactMatch = true) {
        return await this.dbWrapper.dbQueryUp(searchItems, searchKeys, tableName, exactMatch);
    }

    private async _processTagCatalogUrl(response: TQHttpResponse, _callback: any) {
        let newLastModTime = "";
        const locale = await TQUtils.getAppropriateLocale();
        const errCode = TQUtils.mapStatusNumberToErrorCode(response.status);
        const err = new tqtypes.Error(errCode, "", false);
        const tagUrls = TQUtils.parseTagCatalogResponse(response.body, locale);
        try {
            if (tagUrls) {
                const promises: any[] = [];
                const entryCount = await this.dbWrapper.getBrowseDbTableSize(BrowseTables.VARIATION_TAGS);
                let lastModTime = "";
                if (entryCount) {
                    lastModTime = await this.dbWrapper.getBrowseDBLastModTime(BrowseTables.VARIATION_TAGS);
                }
                const noOfTagCatalogs = 3;
                let needsRetry = false;
                let lastModArray: string[] = ["", "", ""];
                if (lastModTime.split("||").length == 3) {
                    lastModArray = lastModTime.split("||");
                }
                promises.push(this.scheduleTask(tqtypes.TaskType.FILE_DOWNLOAD, null, tagUrls.tagUrl, lastModArray[0]));
                promises.push(this.scheduleTask(tqtypes.TaskType.FILE_DOWNLOAD, null, tagUrls.tagAssignmentUrl, lastModArray[1]));
                // promises.push(this.scheduleTask(tqtypes.TaskType.FILE_DOWNLOAD, _callback, tagUrls.tagNamespaceUrl));
                promises.push(this.scheduleTask(tqtypes.TaskType.FILE_DOWNLOAD, null, tagUrls.tagSynonymsUrl, lastModArray[2]));

                let fileResults = await Promise.all(promises);
                if (fileResults.length) {
                    let validityCount = 0;
                    for (const fileResult of fileResults) {
                        if (fileResult.file != "") {
                            validityCount = validityCount + 1;
                        }
                    }
                    if (validityCount != noOfTagCatalogs && validityCount != 0) {
                        // Clear, something's amiss
                        needsRetry = true;
                    }
                } else {
                    // Clear, something's amiss
                    needsRetry = true;
                }

                if (needsRetry) {
                    await this.dbWrapper.browseDb.tagData.clear();
                    lastModTime = "";
                    const retryPromises: any[] = [];
                    retryPromises.push(this.scheduleTask(tqtypes.TaskType.FILE_DOWNLOAD, null, tagUrls.tagUrl, lastModTime));
                    retryPromises.push(this.scheduleTask(tqtypes.TaskType.FILE_DOWNLOAD, null, tagUrls.tagAssignmentUrl, lastModTime));
                    // promises.push(this.scheduleTask(tqtypes.TaskType.FILE_DOWNLOAD, _callback, tagUrls.tagNamespaceUrl));
                    retryPromises.push(this.scheduleTask(tqtypes.TaskType.FILE_DOWNLOAD, null, tagUrls.tagSynonymsUrl, lastModTime));
                    fileResults = await Promise.all(retryPromises);
                }

                if (fileResults.length) {
                    for (const fileResult of fileResults) {
                        newLastModTime = ((newLastModTime + fileResult.lastModTime) as string) + "||";
                    }
                    if (newLastModTime.length >= 2) {
                        newLastModTime = newLastModTime.slice(0, -2);
                    }
                    const dbTags = await this.dbWrapper.processTagCatalogFiles(fileResults);
                    let arrayTagTable = Array.from(dbTags.values());
                    if (dbTags.size == 0) {
                        // Files are empty, get from Database
                        arrayTagTable = await this.queryBrowseDB([], [], BrowseTables.VARIATION_TAGS, true /* Exact match */);
                    } else {
                        // Save in database
                        arrayTagTable = arrayTagTable.sort((a, b) => {
                            return a.tagName.localeCompare(b.tagName);
                        });
                        this.dbWrapper.putTagDataInDB(newLastModTime, arrayTagTable); // Do async
                    }
                    this._tagCatalogLastCheck = new Date().getTime();
                    const tags = await this.dbWrapper.getVariationDataFromFetchedDBData(arrayTagTable, this.initParams.appEntitlement, this._obtainedTokenParams.entitlement);
                    return { tags: tags, err: err };
                }
            }
        } catch (e: any) {
            console.log(e.stack || e);
            err.errCode = tqtypes.ErrorCode.UNKNOWN;
        }
        return { tags: new Map<string, tqtypes.VariationTag>(), err: err };
    }

    private async executePostCatalogTasks() {
        let task = this._postCatalogTaskQueue.pop();
        while (this._postCatalogTaskQueue.length) {
            try {
                task();
            } catch (e) {
                // ...
            }
            task = this._postCatalogTaskQueue.pop();
        }
        if (task) {
            try {
                task();
            } catch (e) {
                // ...
            }
        }
    }

    private async tryLocalCatalogAndFetchIfRequired(task: null | (() => void), initMode = false) {
        // Fire catalog call
        if (this.catalogStatus == CatalogStatus.LOCAL_DONE || this.catalogStatus == CatalogStatus.DONE) {
            // execute post catalog tasks
            if (task) {
                try {
                    task();
                } catch (e) {
                    // ...
                }
            }
            return true;
        }

        const entryCount = await this.dbWrapper.getBrowseDbTableSize(BrowseTables.BROWSE);
        let lastModTime = "";
        if (entryCount) {
            lastModTime = await this.dbWrapper.getBrowseDBLastModTime(BrowseTables.BROWSE);
        }
        if (entryCount && lastModTime != "") {
            this.catalogStatus = CatalogStatus.LOCAL_DONE;
            if (task) {
                try {
                    task();
                } catch (e) {
                    // ...
                }
            }
            return true;
        }
        if (initMode) {
            await this.tryCatalogFetch();
        } else {
            this.tryCatalogFetch();
        }
        this._postCatalogTaskQueue.push(task);
        return false;
    }

    private async tryCatalogFetch(forceFetch = false) {
        // Fire catalog call

        // if (this._initParams.authParams.accessToken == "") {
        //     return;
        // }

        if (forceFetch == false && this.catalogStatus == CatalogStatus.IN_PROGRESS) {
            return;
        }

        if (forceFetch == false && this.catalogStatus == CatalogStatus.DONE) {
            // execute post catalog tasks
            this.executePostCatalogTasks();
            return;
        }

        this.catalogStatus = CatalogStatus.IN_PROGRESS;
        if (this.obtainedTokenParams.catalog == "" && TQUtils.isValidAccessToken(this._initParams.authParams.accessToken)) {
            await this.scheduleTask(tqtypes.TaskType.FONT_CATALOG_URL, null);
        }

        let entryCount = await this.dbWrapper.getBrowseDbTableSize(BrowseTables.BROWSE);
        let lastModTime = "";
        if (entryCount) {
            lastModTime = await this.dbWrapper.getBrowseDBLastModTime(BrowseTables.BROWSE);
        }

        // Extra check
        const res = await this.dbWrapper.getBrowseDBLastModTime(catalogStatusCacheEntry);
        if ((res == "1" && this._obtainedTokenParams.entitlement == tqtypes.Entitlement.TRIAL) || (res == "0" && this._obtainedTokenParams.entitlement == tqtypes.Entitlement.FULL)) {
            // Trial but local is full
            // FULL but local is trial
            // lastModTime = "";
        } else if (res != "1" && res != "0") {
            // Unknown, clear
            lastModTime = "";
        }

        if (TQUtils.isValidAccessToken(this.initParams.authParams.accessToken) == false && this.obtainedTokenParams.catalog == "") {
            let url = "";
            this._initParams.authParams.isStaging ? (url = tqconstants.ServerUrl.FASTER_STAGE) : (url = tqconstants.ServerUrl.FASTER_PROD);
            url += "/try/library/full/library-v2.2.0.json";
            this._obtainedTokenParams.catalog = url;
        }
        if (this.obtainedTokenParams.catalog != "") {
            await this.scheduleTask(tqtypes.TaskType.CATALOG_DOWNLOAD, null, this.obtainedTokenParams.catalog, lastModTime);
            lastModTime = await this.dbWrapper.getBrowseDBLastModTime(BrowseTables.BROWSE);
        }
        entryCount = await this.dbWrapper.getBrowseDbTableSize(BrowseTables.BROWSE);
        if (entryCount) {
            this.catalogStatus = CatalogStatus.DONE;
        } else {
            this.catalogStatus = CatalogStatus.UNINITIALIZED;
        }
        this.executePostCatalogTasks();
    }

    public async clearCacheIfNeeded() {
        const isAnonymous = this.initParams.authParams.userId != "" && !TQUtils.isValidAccessToken(this.initParams.authParams.accessToken);
        if (this.initParams.authParams.userId != "" && (await this.dbWrapper.clearInitCacheIfNeeded(this.initParams.authParams.userId, this.initParams.authParams.appId, isAnonymous)) == true) {
            const region: string = await TQUtils.getAppropriateRegion();
            await this._responseCache.deleteValue(CacheResponseType.LANG_FILTER_DUMMY_ENDPOINT_PREFIX + region + CacheResponseType.LANG_FILTER_DUMMY_ENDPOINT_SUFFIX);
        }
    }

    public static async tryTagCatalogFetch(callback: tqtypes.VariationTagListCallback | undefined, scheduler: TQTaskScheduler) {
        // Do some optimization here and check locale if unchanged then serve
        if (scheduler._tagCatalogLastCheck && Math.abs(new Date().getTime() - scheduler._tagCatalogLastCheck) / 60000 < 600) {
            // Less than ten hours are passed
            if (!callback) {
                return;
            }
        }
        const response = await scheduler.scheduleTask(tqtypes.TaskType.TAG_CATALOG_URL, null);
        TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, "Updated in background now");
        if (callback) {
            callback(response.tags, response.err);
        }
    }

    public static async getMoreFonts(callback: (response: TQFont[]) => boolean, scheduler: TQTaskScheduler, tqFamily: TQFontFamily) {
        const ret = await scheduler.queryBrowseDB([tqFamily.familyName + "##"], ["familyName"], BrowseTables.BROWSE, false);
        const fonts: TQFont[] = [];
        const mapFont = tqFamily.getRawUnorderedMap();
        for (const fontdata of ret) {
            const font = TQUtils.createFontFromDBData(fontdata);
            // Cross check if name matches
            if (font.familyName != tqFamily.familyName) {
                continue;
            }

            if (
                scheduler.initParams.appEntitlement == tqtypes.AppEntitlement.PAID && // If paid app, exclude premium fonts for FREE user
                font.isPremiumFont &&
                scheduler._obtainedTokenParams.entitlement == tqtypes.Entitlement.TRIAL
            ) {
                continue;
            }

            if (mapFont.get(font.postscriptName) == undefined) {
                fonts.push(font);
            }
        }
        callback(fonts);
    }

    public static async getFamilyFromFontId(callback: (response: TQFontFamily) => boolean, scheduler: TQTaskScheduler, tqFontId: tqtypes.TQFontIdentifer) {
        if ((!tqFontId.postscriptName || tqFontId.postscriptName == "") && (!tqFontId.fontId || tqFontId.fontId == "")) {
            callback(new TQFontFamily(""));
        }

        const font = await scheduler.getFontFromLocalOrRemoteInfo(tqFontId.postscriptName ? tqFontId.postscriptName : "", tqFontId.fontId);
        const aggregation = new TQFontAggregation();
        let family: TQFontFamily | undefined = undefined;
        if (font && (font.type == tqtypes.FontType.BYOF || font.type == tqtypes.FontType.APPLICATION)) {
            const fonts = await scheduler.getCachedFontsFromFamilyName(font.familyName, font.type);
            family = TQUtils.getAggregationFromFonts(fonts).getFamilyFromName(font.familyName);
        } else if (font && font.familyName != "") {
            const ret = await scheduler.queryBrowseDB([font.familyName + "##"], ["familyName"], BrowseTables.BROWSE, false);
            TQUtils.getFamiliesFromBulkDBData(ret, aggregation, scheduler.initParams.appEntitlement, scheduler.obtainedTokenParams.entitlement, false);
            family = aggregation.getFamilyFromName(font.familyName);
            if (family) {
                for (const font of family.getEntries()) {
                    if (scheduler._cachedFonts.has(font.postscriptName)) {
                        font.isActiveForUser = true;
                    }
                }
            } else {
                const fonts = await scheduler.getCachedFontsFromFamilyName(font.familyName, font.type);
                if (fonts.length) {
                    family = TQUtils.getAggregationFromFonts(fonts).getFamilyFromName(font.familyName);
                }
            }
        }

        // final check against collab fonts
        if (family == undefined) {
            const fonts = await scheduler.getCollabFamilyFromFontId(tqFontId);
            if (fonts.length) {
                family = TQUtils.getAggregationFromFonts(fonts).getFamilyFromName(fonts[0].familyName);
            }
        }

        if (family == undefined) {
            family = new TQFontFamily("");
        }
        callback(family);
    }

    public static async getRecents(callback: null, scheduler: TQTaskScheduler) {
        setTimeout(() => {
            scheduler.getRecentListSortedAndBroadcastMessage(false);
        }, 0);
    }

    private static async getLangFonts(callback: (response: TQFontAggregation) => boolean, scheduler: TQTaskScheduler, lang: tqtypes.Language) {
        const aggregation = new TQFontAggregation();
        await scheduler.dbWrapper.getAggregationFromLanguageSearch([lang], aggregation, scheduler._obtainedTokenParams.entitlement, scheduler.initParams.appEntitlement);
        callback(aggregation);
    }

    public async getTagCatalogsOnceAvailable(callback: tqtypes.VariationTagListCallback | undefined) {
        // enque
        await this.clearCacheIfNeeded();
        const linkedOperation = () => {
            this._postCatalogTaskQueue.push(() => {
                TQTaskScheduler.tryTagCatalogFetch(callback, this);
            });
            this.tryCatalogFetch();
        };
        // Less than one hour is passed
        const arrayTagTable = await this.queryBrowseDB([], [], BrowseTables.VARIATION_TAGS, true /* Exact match */);
        try {
            if (arrayTagTable.length) {
                const tags = await this.dbWrapper.getVariationDataFromFetchedDBData(arrayTagTable, this.initParams.appEntitlement, this._obtainedTokenParams.entitlement);
                const item = tags.entries().next().value[1] as tqtypes.VariationTag;
                if (tags.size && (await item.displayFont).fontId != "") {
                    // Valid one, return
                    if (callback) {
                        callback(tags, new tqtypes.Error());
                        callback = undefined;
                        TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, "Tags are served from cache, now scheduling the tag catalogs update work");
                        setTimeout(() => {
                            linkedOperation();
                        }, 30000);
                        return;
                    }
                }
            }
        } catch (e) {
            //
        }

        if (callback) {
            TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, "Tags not served from cache, now scheduling the tag catalogs fetch and callbacks");
        }
        linkedOperation();
    }

    public async getMoreFontsOnceAvailable(callback: (response: TQFont[]) => boolean, tqFamily: TQFontFamily) {
        const task = () => {
            TQTaskScheduler.getMoreFonts(callback, this, tqFamily);
        };
        this.tryLocalCatalogAndFetchIfRequired(task);
    }

    public getFamilyOnceAvailable(callback: (response: TQFontFamily) => boolean, tqFontId: tqtypes.TQFontIdentifer) {
        const task = () => {
            TQTaskScheduler.getFamilyFromFontId(callback, this, tqFontId);
        };
        this.tryLocalCatalogAndFetchIfRequired(task);
    }

    public getFontsByLanguageOnceAvailable(callback: (response: TQFontAggregation) => boolean, lang: tqtypes.Language) {
        const task = () => {
            TQTaskScheduler.getLangFonts(callback, this, lang);
        };
        this.tryLocalCatalogAndFetchIfRequired(task);
    }

    public getFamiliesFromSlugsOnceAvailable(callback: (response: any) => boolean, slugs: string[], isActiveForUser: boolean, filter?: string[]) {
        const task = () => {
            TQTaskScheduler.fetchFamiliesPostCatalog(callback, this, slugs, isActiveForUser, filter);
        };
        this.tryLocalCatalogAndFetchIfRequired(task);
    }

    public async getRecommendationsOnceAvailable(type: tqtypes.FontType) {
        if (this._recommendedUrlsMap.get(type) == undefined || this._recommendedUrlsMap.get(type) == "") {
            await this.scheduleTask(tqtypes.TaskType.RECOMMENDATION_URL, null);
        }
        const url = this._recommendedUrlsMap.get(type);

        if (url == undefined || url == "") {
            return { aggregation: new TQFontAggregation(), error: new Error() };
        }

        // if (type == tqtypes.FontType.RECOMMENDED) {
        //     return await this.scheduleTask(tqtypes.TaskType.RECOMMENDED_SLUGS, null, url);
        // } else if (type == tqtypes.FontType.NEWEST) {
        //     return await this.scheduleTask(tqtypes.TaskType.NEWEST_SLUGS, null, url);
        // } else if (type == tqtypes.FontType.TRNENDING) {
        //     return await this.scheduleTask(tqtypes.TaskType.TRENDING_SLUGS, null, url);
        // }
    }

    private getResponseHeader(response: TQHttpResponse, key: string) {
        let lastModTime = "";
        response.headers.forEach(entry => {
            if (entry[0].toLowerCase() == key) {
                lastModTime = entry[1];
            }
        });
        return lastModTime;
    }

    public async handleCacheOnInit() {
        if (this.initParams.authParams.userId.length > 0) {
            await this.clearCacheIfNeeded();
        }
        const cacheLayerFn = (key: string) => {
            return this._responseCache.getInMemoryOrFromResponseCache(key);
        };
        const selectionsResKey =
            CacheResponseType.SELECTIONS_LIST_ENDPOINT_PREFIX + this._initParams.getLanguage() + "_" + this.initParams.authParams.userId + CacheResponseType.SELECTIONS_LIST_ENDPOINT_SUFFIX;
        cacheLayerFn(selectionsResKey);

        const selectionsEtagKey = CacheResponseType.SELECTIONS_ETAG_ENDPOINT_PREFIX + this.initParams.authParams.userId + CacheResponseType.SELECTIONS_ETAG_ENDPOINT_SUFFIX;
        cacheLayerFn(selectionsEtagKey);

        const byofResKey = CacheResponseType.BYOF_LIST_ENDPOINT_PREFIX + this.initParams.authParams.userId + CacheResponseType.BYOF_LIST_ENDPOINT_SUFFIX;
        cacheLayerFn(byofResKey);

        const byofEtagKey = CacheResponseType.BYOF_ETAG_ENDPOINT_PREFIX + this.initParams.authParams.userId + CacheResponseType.BYOF_ETAG_ENDPOINT_SUFFIX;
        cacheLayerFn(byofEtagKey);

        this.fetchCachedEntitlement().then(entitlement => {
            if (entitlement == tqtypes.Entitlement.FULL || entitlement == tqtypes.Entitlement.TRIAL) {
                this._responseCache.matchKey("/slugs_" + entitlement).then(matchedKey => {
                    if (matchedKey != "") {
                        cacheLayerFn(matchedKey);
                    }
                });
            }
        });

        if (TQUtils.isValidAccessToken(this._initParams.authParams.accessToken) == false) {
            try {
                await this.tryLocalCatalogAndFetchIfRequired(null, true);
                TQTaskScheduler.getRecents(null, this);
            } catch (e) {
                // ...
            }
        } else if (this.initParams.runtimeMode != tqtypes.RuntimeMode.FOUND_FONTS) {
            const task = () => {
                TQTaskScheduler.getRecents(null, this);
            };
            this.tryLocalCatalogAndFetchIfRequired(task);
        }
    }

    public async fetchCustomCatalogUrl() {
        const type = this._initParams.applicationFontsCollectionType;
        let types: tqtypes.ApplicationFontsType[] = [];
        if (type) {
            types = types.concat(type);
        }
        if (types.length == 0) {
            return;
        }
        for (const type of types) {
            const taskType = (function (fontsType: tqtypes.ApplicationFontsType) {
                switch (fontsType) {
                    case tqtypes.ApplicationFontsType.PHOTOSHOP:
                        return tqtypes.TaskType.CUSTOM_CATALOG_URL_PHOTOSHOP;
                    default:
                        return tqtypes.TaskType.CUSTOM_CATALOG_URL_VARIABLE;
                }
            })(type);
            const callback: tqtypes.GenericContext<tqtypes.ApplicationFontsType> = {
                root: type,
                retryCount: 1,
            };
            await this.scheduleTask(taskType, callback);
        }
    }

    public async fetchAndprocessCustomCatalog() {
        const type = this._initParams.applicationFontsCollectionType;
        let types: tqtypes.ApplicationFontsType[] = [];
        if (type) {
            types = types.concat(type);
        }
        const finalErr = new tqtypes.Error();
        const finalAggregation = new TQFontAggregation();
        if (types.length == 0) {
            finalErr.errCode = tqtypes.ErrorCode.INVALID_CONFIGURATION;
        }
        if (types.length == 0 || TQUtils.isValidAccessToken(this.initParams.authParams.accessToken) == false) {
            return { aggregation: new TQFontAggregation(), error: finalErr };
        }

        for (const type of types) {
            const taskType = (function (fontsType: tqtypes.ApplicationFontsType) {
                switch (fontsType) {
                    case tqtypes.ApplicationFontsType.PHOTOSHOP:
                        return tqtypes.TaskType.CUSTOM_CATALOG_URL_PHOTOSHOP;
                    default:
                        return tqtypes.TaskType.CUSTOM_CATALOG_URL_VARIABLE;
                }
            })(type);
            const callback: tqtypes.GenericContext<tqtypes.ApplicationFontsType> = {
                root: type,
                retryCount: 1,
            };
            const err = await this.scheduleTask(taskType, callback);
            const params = this.obtainedTokenParams.mapOfCustomCatalogParams.get(type);
            if (err.errCode == tqtypes.ErrorCode.NONE && params && params.catalog != "") {
                const { aggregation, error } = await this.scheduleTask(tqtypes.TaskType.CUSTOM_CATALOG_DOWNLOAD, callback, params.catalog);
                finalAggregation.merge(aggregation);
            }

            if (err.errCode != tqtypes.ErrorCode.NONE) {
                finalErr.errCode = err.errCode;
            }
        }
        return { aggregation: finalAggregation, error: finalErr };
    }

    private async _handleCustomBrowseCatalogUrlResponse(response: TQHttpResponse, _callback: any) {
        const err = new tqtypes.Error();
        if (response.status != tqconstants.ResponseStatus.OK) {
            err.errCode = TQUtils.mapStatusNumberToErrorCode(response.status);
        } else {
            const params = new tqtypes.ObtainedTokenParams();
            if (response.body["links"]) {
                params.catalog = response.body["links"]["catalog"];
                params.token = response.body["token"];
                params.genericdownloadUrl = response.body["links"]["download"];
                const first = params.token.indexOf("=");
                const last = params.token.indexOf("~");
                if (first != -1 && last != -1) {
                    params.expiry = +params.token.substring(first + 1, last);
                }

                const type = _callback as tqtypes.GenericContext<tqtypes.ApplicationFontsType>;
                if (type.root) {
                    this.obtainedTokenParams.mapOfCustomCatalogParams.set(type.root, params);
                }
            } else {
                err.errCode = tqtypes.ErrorCode.UNKNOWN;
            }
        }
        return err;
    }

    private async _handleCustomBrowseCatalogResponse(response: TQHttpResponse, _callback: any) {
        const err = new tqtypes.Error();
        let aggregation = new TQFontAggregation();

        if (response.status >= tqconstants.ResponseStatus.BAD_REQUEST) {
            // ... handle error
            err.errCode = TQUtils.mapStatusNumberToErrorCode(response.status);
        } else if (response.body != "") {
            const lastModTime: string = this.getResponseHeader(response, "last-modified");
            const time = performance.now();
            try {
                const type = _callback as tqtypes.GenericContext<tqtypes.ApplicationFontsType>;
                if (type.root) {
                    const parser = new TQCustomCatalogHandler(response.body, type.root);
                    aggregation = parser.parse(this._cachedFonts);
                    TQUtils.setDisplayFontForEachFamilyIfNeeded(aggregation);
                }
            } catch (e) {
                // ..
            }
            TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, aggregation);
            TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, this.cachedFonts());
            // for (const [name, family] of aggregation.getEntries()) {
            //     console.log("==========================");
            //     console.log(family.familyName);
            //     for (const font of family.getEntries()) {
            //         console.log(font.styleName);
            //         console.log(font.varAxisValues);
            //     }
            // }
            TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `In-Memory DB put operations done. Took ${Math.round(performance.now() - time)} milliseconds.`);
        }

        return { aggregation: aggregation, error: err };
    }

    private async _handleCatalogResponse(response: TQHttpResponse, _callback: any) {
        const browseFonts: any[] = [];
        const memBrowseFonts: any[] = [];

        if (response.status >= tqconstants.ResponseStatus.BAD_REQUEST) {
            // ... handle error
        } else {
            // ...
            if (response.body == "") {
                return;
            }
            const lastModTime: string = this.getResponseHeader(response, "last-modified");
            await this.dbWrapper.clearBrowseDbTables(BrowseTables.BROWSE);

            // For catalog 2.2.0

            // const map1 = new Set<String>();
            // const map2 = new Set<String>();
            // const map3 = new Set<string>();

            response.body.forEach((font: any) => {
                let isPremium = false;
                const availabilityArray: any[] = font["availability"];
                const result = availabilityArray.find(name => name == "trial");
                result == undefined ? (isPremium = true) : "";
                // no trial hence this is premium

                const typekitId = font["sync_font_id"] as string;
                // Now get the variation id from typekitId
                let varId = typekitId.split("-").slice(0, 2).pop();
                varId == undefined ? (varId = typekitId) : "";

                const locFamilyNames: string[] = [];
                const familyNames: string[] = [];

                const locStyleName: string[] = [];
                const styleNames: string[] = [];

                const supportedLangs: string[] = [];

                const fullNames = new Map<string, string>();
                for (const entry in font["localized_family_name"]) {
                    if (font["localized_family_name"][entry]) {
                        const name = (font["localized_family_name"][entry] as string).trim().replace(/\s+/g, " ");
                        // familyNames.push(name);
                        locFamilyNames.push(name + "##" + entry);
                        fullNames.set(entry, name);
                        // map1.add(entry);
                        familyNames.push(...name.toLowerCase().split(" "));
                    }
                }

                for (const entry in font["localized_subfamily_name"]) {
                    if (font["localized_subfamily_name"][entry]) {
                        const name = (font["localized_subfamily_name"][entry] as string).trim().replace(/\s+/g, " ");
                        styleNames.push(...name.toLowerCase().split(" "));
                        styleNames.push(name.toLowerCase());
                        locStyleName.push(name + "##" + entry);
                        const savedFamName = fullNames.get(entry);
                        savedFamName != undefined ? fullNames.set(entry, savedFamName + " " + name) : "";
                    }
                }

                for (const fullname of fullNames) {
                    familyNames.push(fullname[1].toLowerCase());
                }

                for (const lang of font["language_support"]) {
                    supportedLangs.push(lang);
                    // map3.add(lang);
                }

                // map2.add(font["default_language"] as string);

                browseFonts.push({
                    psName: font["postscript_name"] as string,
                    tkId: typekitId,
                    sortIndex: font["sort_order"] as number,
                    lang: font["default_language"] as string,
                    supportedLangs: supportedLangs,
                    premium: isPremium,
                    display: font["is_display_font"] as boolean,
                    variationId: varId,
                    familyName: locFamilyNames,
                    styleName: locStyleName,
                    familySlug: font["family_slug"] as string,
                    locFamilyName: [...new Set(familyNames)],
                    locStyleName: [...new Set(styleNames)],
                });

                memBrowseFonts.push({
                    psName: font["postscript_name"] as string,
                    tkId: typekitId,
                    sortIndex: font["sort_order"] as number,
                    lang: font["default_language"] as string,
                    supportedLangs: supportedLangs,
                    premium: isPremium,
                    display: font["is_display_font"] as boolean,
                    variationId: varId,
                    familyName: locFamilyNames,
                    styleName: locStyleName,
                    familySlug: font["family_slug"] as string,
                    locFamilyName: [...new Set(familyNames)],
                    locStyleName: [...new Set(styleNames)],
                });
            });
            // console.log(map1);
            // console.log(map3);

            const time = performance.now();
            await this.dbWrapper.clearBrowseDbTables(BrowseTables.BROWSE);
            await this.dbWrapper.bulkPutBrowseMemDbData(BrowseTables.BROWSE, memBrowseFonts);
            TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `In-Memory DB put operations done. Took ${Math.round(performance.now() - time)} milliseconds.`);
            this.dbWrapper.commitMemDBTime(lastModTime, BrowseTables.BROWSE);
            this.dbWrapper.commitMemDBTime(this._obtainedTokenParams.entitlement == tqtypes.Entitlement.FULL ? "1" : "0", catalogStatusCacheEntry);

            const putInIDBIfApplicable = async () => {
                const time = performance.now();

                let perChunk = 50; // items per chunk
                if (!this._timer.isPaused()) {
                    perChunk = 50;
                }
                let dataToPut = browseFonts.splice(-perChunk);
                while (dataToPut.length > 0) {
                    await this.dbWrapper.bulkPutIDBBrowseDbData(BrowseTables.BROWSE, dataToPut);
                    if (this._timer.isPaused()) {
                        perChunk = 50;
                    } else {
                        perChunk = 50;
                    }
                    dataToPut = browseFonts.splice(-perChunk);
                    TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, `IDB Put operations done. Took ${Math.round(performance.now() - time)} milliseconds with result for chunk ${perChunk}`);
                }
                this.dbWrapper.commitIDBTime(lastModTime, BrowseTables.BROWSE);
                this.dbWrapper.commitIDBTime(this._obtainedTokenParams.entitlement == tqtypes.Entitlement.FULL ? "1" : "0", catalogStatusCacheEntry);
            };
            putInIDBIfApplicable(); // execute async
        }
    }

    public async getSlugNames(searchItems: string[]) {
        const queryPromises: any = [];
        searchItems.forEach((searchItem: string) => {
            const contextCopy: tqtypes.GenericContext<tqtypes.FetchFamilyContext> = {
                root: {
                    familySlug: searchItem,
                },
                retryCount: 1,
            };
            queryPromises.push(this.scheduleTask(tqtypes.TaskType.SEARCH_FAMILY, contextCopy));
        });

        const queryResults: string[] = await Promise.all(queryPromises);
        const querySet = new Set<string>();
        for (const slugs of queryResults) {
            for (const slug of slugs) {
                querySet.add(slug);
            }
        }
        return querySet;
    }

    public async serverQueryGetSlugPromises(querySet: Set<string>) {
        const slugPromises: any = [];
        for (const slug of querySet) {
            const contextCopy: tqtypes.GenericContext<tqtypes.FetchFamilyContext> = {
                root: {
                    familySlug: slug,
                },
                retryCount: 1,
            };
            slugPromises.push(this.scheduleTask(tqtypes.TaskType.FETCH_FAMILY, contextCopy));
        }
        return slugPromises;
    }

    public static async fetchFamiliesPostCatalog(callback: (response: any) => boolean, scheduler: TQTaskScheduler, slugs: string[], isActiveForUser: boolean, filter: string[] = []) {
        const result = await scheduler._fetchDefaultFamiliesData(slugs, isActiveForUser, filter);
        callback(result);
    }

    public async _fetchDefaultFamiliesData(slugs: string[], isActiveForUser: boolean, filter: string[] = []) {
        const aggregation = new TQFontAggregation();

        const updatedCachedIfNeeded = async () => {
            for (const [familyName, family] of aggregation.getEntries()) {
                for (const font of family.getEntries()) {
                    this._cachedFonts.set(font.postscriptName, font);
                }
            }
        };

        let dbResults: any[] = [];
        const slugKey = slugs.join(",");
        const entitlement = await this.fetchCachedEntitlement();
        const sha256sumOfSlugs = "/slugs_" + entitlement + (await TQUtils.getHash(HashAlgo.SHA256, slugKey));
        dbResults = await this._responseCache.getInMemoryOrFromResponseCache(sha256sumOfSlugs);

        if (dbResults == undefined || dbResults.length == 0) {
            dbResults = await this.queryBrowseDB(slugs, ["familySlug"], BrowseTables.BROWSE, true);
            this._responseCache.putValue(sha256sumOfSlugs, dbResults);
        }

        if (filter.length) {
            dbResults = dbResults.filter((entry: any) => {
                for (let searchItem of filter) {
                    searchItem = searchItem.toLowerCase();
                    if (entry.locFamilyName.join("#").indexOf(searchItem) == -1 && entry.locStyleName.join("#").indexOf(searchItem) == -1) {
                        return false;
                    }
                }
                return true;
            });
        }
        TQUtils.getFamiliesFromBulkDBData(dbResults, aggregation, this.initParams.appEntitlement, this.obtainedTokenParams.entitlement, isActiveForUser);
        if (dbResults.length) {
            updatedCachedIfNeeded();
            return { aggregation: aggregation, error: new tqtypes.Error() };
        }
        // }

        const promises: any = [];
        slugs.forEach((slug: string) => {
            const contextCopy: tqtypes.GenericContext<tqtypes.FetchFamilyContext> = {
                root: {
                    familySlug: slug,
                },
                retryCount: 1,
            };
            promises.push(this.scheduleTask(tqtypes.TaskType.FETCH_FAMILY, contextCopy));
        });

        const results = await Promise.all(promises);
        const fontAggregation: TQFontAggregation = new TQFontAggregation();
        results.forEach((familyInfos: any) => {
            if (familyInfos == "") {
                return;
            }
            const displayFontPsName: string = familyInfos["display_font"]["postscript_name"];
            const fontsArray: JSON[] = familyInfos["fonts"];
            TQUtils.getFamiliesFromFontsArrayJSON(
                fontsArray,
                tqtypes.FontType.DEFAULT,
                this.obtainedTokenParams.entitlement,
                fontAggregation,
                displayFontPsName,
                null,
                this.initParams.appEntitlement,
                isActiveForUser,
                filter
            );
        });
        const err = new tqtypes.Error();
        updatedCachedIfNeeded();
        return { aggregation: fontAggregation, error: err };
    }

    private async _fetchFamilyResponse(response: TQHttpResponse, _callback: any) {
        if (response.status != tqconstants.ResponseStatus.OK) {
            // TODO: Pranay, do the error handling
            return "";
        } else {
            return response.body;
        }
    }

    private async _searchFamilyResponse(response: TQHttpResponse, _callback: any) {
        const slugs: string[] = [];
        if (response.status != tqconstants.ResponseStatus.OK) {
            // ...
            // TODO: Pranay, do the error handling
        } else {
            const famJson: [] = response.body;
            for (const family of famJson) {
                try {
                    const slug: string = family["slug"] as string;
                    slugs.push(slug);
                } catch (e) {
                    // ...
                }
            }
        }
        return slugs;
    }

    private _activatedResponse = (response: TQHttpResponse, _callback: any) => {
        if (response.status != tqconstants.ResponseStatus.OK) {
            // ...
            // TODO: Pranay, do the error handling
            const dispatchCallbackFn = async () => {
                // const errCode = TQUtils.mapStatusNumberToErrorCode(response.status);
                // const err = new tqtypes.Error(errCode, "Activated fonts Error", false);
                return "";
            };
            dispatchCallbackFn();
        } else {
            const res = response.body;
            let etag: string = this.getResponseHeader(response, "etag");
            etag = TQUtils.parseETagValue(etag);
            return { res, etag };
        }
        return true;
    };

    public async getCachedByof(etag: string, fontAggregation: TQFontAggregation, isRequested: boolean) {
        // Check what we cached last time
        const psNames: string[] = [];
        const cacheLayerFn = (key: string) => {
            return etag == "" ? this._responseCache.getInMemoryOrFromResponseCache(key) : this._responseCache.getValue(key);
        };
        const byofRes = await cacheLayerFn(CacheResponseType.BYOF_LIST_ENDPOINT_PREFIX + this.initParams.authParams.userId + CacheResponseType.BYOF_LIST_ENDPOINT_SUFFIX);
        const cachedByofEtag = await cacheLayerFn(CacheResponseType.BYOF_ETAG_ENDPOINT_PREFIX + this.initParams.authParams.userId + CacheResponseType.BYOF_ETAG_ENDPOINT_SUFFIX);

        if ((etag == "" || cachedByofEtag == etag) && byofRes) {
            // Use the cached one
            const fonts = TQUtils.getFontsFromByofData(byofRes);
            for (const font of fonts) {
                this._allByofFontHashes.set(font.fontId, font); // set identifier for BYOF font
            }
            TQUtils.getFamiliesFromByofData(fonts, fontAggregation, []);
            fontAggregation = TQUtils.setDisplayFontForEachFamilyIfNeeded(fontAggregation);
            fontAggregation.getEntries().forEach(family => {
                family.getEntries().forEach(font => {
                    psNames.push(font.postscriptName);
                    this._cachedFonts.set(font.postscriptName, font);
                    this._cachedFonts.set(font.fontId, font);
                });
            });
            if (isRequested) {
                // Cache in-memory psNames and ETag if it was requested
                this._byofFonts = new Set(psNames);
                if (etag != "") {
                    this._byofsEtag = etag;
                } else {
                    this._byofsEtag = cachedByofEtag;
                }
            }
            return true;
        }
        return false;
    }

    private async _byofCountResponse(response: TQHttpResponse, _callback: any) {
        const res = {
            aggregation: new TQFontAggregation(),
            error: new tqtypes.Error(),
        };
        const countContext = _callback as tqtypes.GenericContext<tqtypes.AggregationContext>;
        let fontAggregation: TQFontAggregation = new TQFontAggregation();

        if (response.status != tqconstants.ResponseStatus.OK) {
            // ...
            if (response.status == tqconstants.ResponseStatus.UNAUTHORIZED) {
                countContext.retryCount = 0;
            } else {
                countContext.retryCount = countContext.retryCount - 1;
            }

            if (countContext.retryCount > 0) {
                const ret = (await this.scheduleTask(tqtypes.TaskType.BYOF_COUNT, countContext)) as Promise<typeof res>;
                return ret;
            } else {
                const errCode = TQUtils.mapStatusNumberToErrorCode(response.status);
                const err = new tqtypes.Error(errCode, "BYOF fonts Error", false);
                return { aggregation: fontAggregation, error: err };
            }
        } else {
            let totalCount: number = response.body["total"];
            if (totalCount == NaN) {
                totalCount = 0;
            } else {
                countContext.root.page = Math.floor(totalCount / 100) + 1;
                countContext.root.perPage = 100;
            }

            let etag: string = this.getResponseHeader(response, "etag");
            etag = TQUtils.parseETagValue(etag);

            // Check what we cached last time
            const cachedValid = await this.getCachedByof(etag, fontAggregation, countContext.root.isRequested);

            if (cachedValid) {
                this._byofFontsLastCheck = new Date().getTime();
                const err = new tqtypes.Error();
                return { aggregation: fontAggregation, error: err };
            } else {
                // Schedule all calls one by one
                const promises: any = [];

                for (let pageIndex = 1; pageIndex < countContext.root.page; pageIndex++) {
                    const contextCopy: tqtypes.GenericContext<tqtypes.AggregationContext> = {
                        root: {
                            page: pageIndex + 1,
                            perPage: countContext.root.perPage,
                            isRequested: countContext.root.isRequested,
                        },
                        retryCount: countContext.retryCount,
                    };
                    promises.push(this.scheduleTask(tqtypes.TaskType.BYOF_LISTS, contextCopy));
                }
                const results: any[] = [{ res: response.body, etag: etag }];
                if (promises.length) {
                    results.push(...(await Promise.all(promises)));
                }

                let allbyofFontsJson: any[] = [];
                results.forEach((entry: any) => {
                    const fontInfos = entry.res.fonts;
                    if (allbyofFontsJson.length == 0) {
                        allbyofFontsJson = fontInfos;
                    } else {
                        allbyofFontsJson = [...allbyofFontsJson, ...fontInfos];
                    }
                    if (fontInfos == "") {
                        return;
                    }
                    this._allByofFontHashes.clear();
                    const fonts = TQUtils.getFontsFromByofData(fontInfos);
                    for (const font of fonts) {
                        this._allByofFontHashes.set(font.fontId, font); // set identifier for BYOF font
                    }
                    TQUtils.getFamiliesFromByofData(fonts, fontAggregation, []);
                });

                const psNames: string[] = [];
                fontAggregation = TQUtils.setDisplayFontForEachFamilyIfNeeded(fontAggregation);
                fontAggregation.getEntries().forEach(family => {
                    family.getEntries().forEach(font => {
                        psNames.push(font.postscriptName);
                        this._cachedFonts.set(font.postscriptName, font);
                        this._cachedFonts.set(font.fontId, font);
                    });
                });

                this._byofFontsLastCheck = new Date().getTime();
                // Just cache them silently
                this._responseCache.putValue(CacheResponseType.BYOF_LIST_ENDPOINT_PREFIX + this.initParams.authParams.userId + CacheResponseType.BYOF_LIST_ENDPOINT_SUFFIX, allbyofFontsJson);
                this._responseCache.putValue(CacheResponseType.BYOF_ETAG_ENDPOINT_PREFIX + this.initParams.authParams.userId + CacheResponseType.BYOF_ETAG_ENDPOINT_SUFFIX, etag);

                if (countContext.root.isRequested) {
                    // Cache in-memory psNames and ETag if it was requested
                    this._byofFonts = new Set(psNames);
                    this._byofsEtag = etag;
                } else {
                    setTimeout(() => {
                        this.fireFontUpdates();
                    }, 1000);
                }

                const err = new tqtypes.Error();
                return { aggregation: fontAggregation, error: err };
            }
        }
    }

    private _byofListResponse = (response: TQHttpResponse, _callback: any) => {
        if (response.status != tqconstants.ResponseStatus.OK) {
            // ...
            // TODO: Pranay, do the error handling
            const dispatchCallbackFn = async () => {
                // const errCode = TQUtils.mapStatusNumberToErrorCode(response.status);
                // const err = new tqtypes.Error(errCode, "Activated fonts Error", false);
                return "";
            };
            dispatchCallbackFn();
        } else {
            const res = response.body;
            let etag: string = this.getResponseHeader(response, "etag");
            etag = TQUtils.parseETagValue(etag);
            return { res, etag };
        }
        return true;
    };

    private async _activatedPollResponse(response: TQHttpResponse, _callback: any) {
        const errCode = TQUtils.mapStatusNumberToErrorCode(response.status);
        const setPollIntervalIfNeeded = async () => {
            const retryValue = this.getResponseHeader(response, "retry-after");
            const requiredDelay = parseInt(retryValue);
            if (retryValue != "" && requiredDelay > tqconstants.defaultPollInterval) {
                // Honor Retry-After if present and greater than 10 ticks
                this._timer.getTask(selectionsPollTaskId).tickTime = requiredDelay;
            } else if (errCode != tqtypes.ErrorCode.NONE && response.status >= 500) {
                // Exponential backoff if error case and absent
                this._timer.getTask(selectionsPollTaskId).tickTime = this._timer.getTask(selectionsPollTaskId).tickTime * 2;
            } else if (retryValue == "" && errCode == tqtypes.ErrorCode.NONE && this._timer.getTask(selectionsPollTaskId).tickTime != this.initParams.selectionPollInterval) {
                this._timer.getTask(selectionsPollTaskId).tickTime = this.initParams.selectionPollInterval;
            }
        };

        await setPollIntervalIfNeeded();

        if (errCode != tqtypes.ErrorCode.NONE) {
            // ...
            if (errCode == tqtypes.ErrorCode.AUTH_FAILURE) {
                this.handleAuthFailure();
            }
        } else {
            const res = response.body;
            let etag: string = this.getResponseHeader(response, "etag");
            etag = TQUtils.parseETagValue(etag);

            const dbData = await this.dbWrapper.getSelectionsData();
            if (dbData && this._selectionsEtag != "") {
                if (dbData.etag == etag || etag == "") {
                    // No change
                } else {
                    // Change deteced, trigger activated poll
                    // code to be executed on each run
                    const callback: tqtypes.GenericContext<tqtypes.AggregationContext> = {
                        root: {
                            page: 1,
                            perPage: 100,
                            isRequested: false,
                        },
                        retryCount: 1,
                    };
                    this.scheduleTask(tqtypes.TaskType.ACTIVATED_FONTS_COUNT, callback);
                    // this._activatedCountResponse(response, _callback);
                }
            }
        }
        return true;
    }

    private async _byofPollResponse(response: TQHttpResponse, _callback: any) {
        const errCode = TQUtils.mapStatusNumberToErrorCode(response.status);
        const setPollIntervalIfNeeded = async () => {
            const retryValue = this.getResponseHeader(response, "retry-after");
            const requiredDelay = parseInt(retryValue);
            if (retryValue != "" && requiredDelay > tqconstants.defaultPollInterval) {
                // Honor Retry-After if present and greater than 10 ticks
                this._timer.getTask(byofPollTaskId).tickTime = requiredDelay;
            } else if (errCode != tqtypes.ErrorCode.NONE && response.status >= 500) {
                // Exponential backoff if error case and absent
                this._timer.getTask(byofPollTaskId).tickTime = this._timer.getTask(byofPollTaskId).tickTime * 2;
            } else if (retryValue == "" && errCode == tqtypes.ErrorCode.NONE && this._timer.getTask(byofPollTaskId).tickTime != this.initParams.selectionPollInterval) {
                this._timer.getTask(byofPollTaskId).tickTime = this.initParams.selectionPollInterval;
            }
        };

        await setPollIntervalIfNeeded();

        if (errCode != tqtypes.ErrorCode.NONE) {
            // ...
            if (errCode == tqtypes.ErrorCode.AUTH_FAILURE) {
                this.handleAuthFailure();
            }
        } else {
            const res = response.body;
            let etag: string = this.getResponseHeader(response, "etag");
            etag = TQUtils.parseETagValue(etag);

            const byofEtag = this._byofsEtag;

            if (byofEtag != "") {
                if (byofEtag == etag || etag == "") {
                    // No change
                } else {
                    // Change deteced, trigger byof poll
                    // code to be executed on each run
                    const callback: tqtypes.GenericContext<tqtypes.AggregationContext> = {
                        root: {
                            page: 1,
                            perPage: 100,
                            isRequested: false,
                        },
                        retryCount: 1,
                    };
                    this.scheduleTask(tqtypes.TaskType.BYOF_COUNT, callback);
                    // this._activatedCountResponse(response, _callback);
                }
            }
        }
        return true;
    }

    private async _processFontDownloadResponse(request: TQHttpRequest, response: TQHttpResponse, _callback: any) {
        const callback = _callback as tqtypes.GenericContext<tqtypes.InternalContext>;
        const errCode = TQUtils.mapStatusNumberToErrorCode(response.status);

        let isNotified = false;
        const tkId = callback.root.fontInfo.fontId;
        const errNotified = new tqtypes.Error(errCode, "", false);
        const fontNotified = callback.root.fontInfo;

        const dispatchCallbackFn = async () => {
            callback.root.downloadCB(fontNotified, errNotified);
        };

        if (response.status != tqconstants.ResponseStatus.OK) {
            if (response.status == tqconstants.ResponseStatus.NOT_FOUND && tkId != "" && callback.retryCount != 0) {
                // Fallback to cctypekit
                callback.retryCount = 0;
                let url = "";
                if (this._obtainedTokenParams.genericdownloadUrl != "" && this._obtainedTokenParams.fasterEndPointToken != "") {
                    url = this._obtainedTokenParams.genericdownloadUrl;
                    url += tkId + "?__token__=" + this._obtainedTokenParams.fasterEndPointToken;
                } else {
                    url = this._initParams.authParams.isStaging ? tqconstants.ServerUrl.TYPEKIT_STAGE : tqconstants.ServerUrl.TYPEKIT_PROD;
                    url += tqconstants.ServerAPI.TYPEKIT_DOWNLOAD;
                    url += tkId + "/download";
                }
                await this.scheduleTask(tqtypes.TaskType.FONT_DOWNLOAD, callback, url);
            } else if (callback.root.downloadCB != null) {
                isNotified = true;
                errNotified.errMsg = "Font Error";
                dispatchCallbackFn();
            }
        } else {
            if (false == request.isBlob) {
                // Fallback to cctypekit
                callback.retryCount = 0;
                const url: string = response.body[0];
                if (url != "") {
                    await this.scheduleTask(tqtypes.TaskType.FONT_DOWNLOAD, callback, url);
                    isNotified = true;
                } else {
                    errNotified.errCode = tqtypes.ErrorCode.UNKNOWN;
                }
            } else {
                const fontStream = await new Response(response.body).arrayBuffer();
                callback.root.fontInfo.fontBuffer = new Uint8Array(fontStream);

                if (this.initParams.runtimeMode != tqtypes.RuntimeMode.FOUND_FONTS) {
                    try {
                        const font = new TQFontParser(callback.root.fontInfo.fontBuffer);
                        callback.root.fontInfo.characterSet = font.charSet;
                    } catch (e) {
                        // ...
                    }
                }

                if (false == (await TQUtils.checkIfValidFontStream(callback.root.fontInfo.fontBuffer))) {
                    // ...
                    callback.root.fontInfo.fontBuffer = TQUtils.reversePartArray(callback.root.fontInfo.fontBuffer, 0, 1040);
                }

                // Cache the font data in ResponseStorageCache
                this._foundFontsHandler.putFontBufferInCache(callback.root.fontInfo, false);

                if (callback.root.fontInfo.type == tqtypes.FontType.BYOF) {
                    try {
                        const fontParser = new TQFontParser(callback.root.fontInfo.fontBuffer);
                        if (fontParser.font) {
                            const parsedByofFont = fontParser.getTQFont();
                            if (callback.root.fontInfo.postscriptName != "") {
                                const fontKey = CacheResponseType.BYOF_SCRIPT_ENDPOINT_PREFIX + callback.root.fontInfo.fontId + downloaderVersion + CacheResponseType.BYOF_SCRIPT_ENDPOINT_SUFFIX;
                                const byofScriptJson = await this._responseCache.getValue(fontKey);
                                if (callback.root.fontInfo.fontId != "" && byofScriptJson) {
                                    callback.root.fontInfo.script = byofScriptJson;
                                } else {
                                    callback.root.fontInfo.script = fontParser.computeFontScript(callback.root.fontInfo.postscriptName);
                                    this._responseCache.putValue(fontKey, callback.root.fontInfo.script);
                                }
                                callback.root.fontInfo.addSupportedLanguage(callback.root.fontInfo.script);
                            }
                        }
                    } catch (e) {
                        // ..
                    }
                }

                // callback.root.fontInfo.fontBuffer = TQUtils.ReversePartArray(callback.root.fontInfo.fontBuffer, 0, 1041);
                if (callback.root.downloadCB != null) {
                    isNotified = true;
                    errNotified.errCode = tqtypes.ErrorCode.NONE;
                    dispatchCallbackFn();
                }

                if (this._initParams.diskCache == tqtypes.DiskCacheType.OPFS) {
                    const success = await TQTaskScheduler._diskCache.addFile(
                        callback.root.fontInfo.postscriptName,
                        callback.root.fontInfo.postscriptName,
                        callback.root.fontInfo.fontBuffer.length,
                        callback.root.fontInfo.fontBuffer
                    );
                    if (success) {
                        callback.root.fontInfo.filePath = DOC_FOLDER_EMFS + callback.root.fontInfo.postscriptName;
                    }
                }
            }
        }

        if (isNotified) {
            return true;
        } else {
            return errNotified;
        }
    }

    private _processTokenResponse: (response: TQHttpResponse, _callback: any) => boolean = (response: TQHttpResponse, _callback: any) => {
        let tokenFetchCtx: tqtypes.GenericContext<[] | tqtypes.InternalContext>;
        this._nonBlockingTokenCallStatus = false;

        if (TQUtils.isValidAccessToken(this._initParams.authParams.accessToken) == false) {
            if (response.body["links"] && response.body["links"]["catalog"]) {
                this._obtainedTokenParams.catalog = response.body["links"]["catalog"];
            }
            if (this._obtainedTokenParams.catalog == "") {
                let url = "";
                this._initParams.authParams.isStaging ? (url = tqconstants.ServerUrl.FASTER_STAGE) : (url = tqconstants.ServerUrl.FASTER_PROD);
                url += "/try/library/full/library-v2.2.0.json";
                this._obtainedTokenParams.catalog = url;
            }
        }

        if (response.status != tqconstants.ResponseStatus.OK) {
            if (response.taskType == tqtypes.TaskType.TOKEN_FETCH) {
                tokenFetchCtx = _callback as tqtypes.GenericContext<[]>;
            } else {
                tokenFetchCtx = _callback as tqtypes.GenericContext<tqtypes.InternalContext>;
            }

            if (response.status == tqconstants.ResponseStatus.UNAUTHORIZED || response.status == tqconstants.ResponseStatus.FORBIDDEN) {
                this._accessTokenBlockedSet.add(this._initParams.authParams.accessToken);
                tokenFetchCtx.retryCount = 0;
            } else {
                tokenFetchCtx.retryCount = tokenFetchCtx.retryCount - 1;
            }

            if (tokenFetchCtx.retryCount) {
                this.scheduleTask(response.taskType, tokenFetchCtx);
            } else {
                const errCode = TQUtils.mapStatusNumberToErrorCode(response.status);
                if (this._status != tqtypes.StatusCode.ERROR_OCCURED) {
                    this._status = tqtypes.StatusCode.ERROR_OCCURED;
                    this._initParams.statusUpdateCallback(this._status, new tqtypes.Error(errCode, "Token Error", true));
                }
            }
        } else {
            const anonymousToken = response.body["auth_preview_token"];
            let resToken: string = response.body["x_af_auth_token"];
            const trialToken: string = response.body["auth_free_full_token"];

            if (response.body["token"]) {
                this._obtainedTokenParams.fasterEndPointToken = response.body["token"];
            }

            if (response.body["links"] && response.body["links"]["download"]) {
                this._obtainedTokenParams.genericdownloadUrl = response.body["links"]["download"];
            }

            const entitlement: string = response.body["library"];
            const entitlementLevel = entitlement == undefined ? tqtypes.Entitlement.ANONYMOUS : (entitlement as tqtypes.Entitlement);
            if (entitlementLevel != undefined) {
                this._obtainedTokenParams.entitlement = entitlementLevel;
                TQInitParams.cachedEntitlement = entitlementLevel;
                const fontKey = CacheResponseType.ENTITLEMENT_ENDPOINT_PREFIX + encodeURIComponent(this.initParams.authParams.userId) + CacheResponseType.ENTITLEMENT_ENDPOINT_SUFFIX;
                this.responseCache.putValue(fontKey, entitlementLevel);
            }

            if (entitlementLevel == tqtypes.Entitlement.TRIAL && trialToken != "undefined" && trialToken != "") {
                resToken = trialToken;
            }

            if (resToken == undefined && anonymousToken) {
                resToken = anonymousToken;
            }

            this._obtainedTokenParams.token = resToken;
            const first = resToken.indexOf("=");
            const last = resToken.indexOf("~");
            if (first != -1 && last != -1) {
                this._obtainedTokenParams.expiry = +resToken.substring(first + 1, last);
            }

            this._status = tqtypes.StatusCode.DONE;

            if (response.taskType == tqtypes.TaskType.TOKEN_FETCH) {
                this._initParams.statusUpdateCallback(this._status, new tqtypes.Error());
            } else {
                tokenFetchCtx = _callback as tqtypes.GenericContext<tqtypes.InternalContext>;
                tokenFetchCtx.retryCount = tokenFetchCtx.retryCount - 1;
                if (tokenFetchCtx.retryCount) {
                    this.scheduleTask(tqtypes.TaskType.FONT_DOWNLOAD, _callback);
                } else {
                    // Error occured, notify the error fonts
                }
            }
            // Fire catalog
            if (this.initParams.runtimeMode != tqtypes.RuntimeMode.FOUND_FONTS) {
                this.tryLocalCatalogAndFetchIfRequired(null);
            }
            // this.scheduleTask(tqtypes.TaskType.RECOMMENDATION_URL, null);
        }
        return true;
    };

    private async getCachedFontsFromFamilyName(familyName: string, type: tqtypes.FontType) {
        const fonts: TQFont[] = [];
        for (const [psName, cachedFont] of this._cachedFonts) {
            if (cachedFont.familyName == familyName && cachedFont.type == type) {
                fonts.push(cachedFont);
            }
        }
        return fonts;
    }

    private async getCollabFamilyFromFontId(tqFontId: tqtypes.TQFontIdentifer) {
        let familyName = "";
        let fontIfExists = tqFontId.fontId ? this._collabFonts.get(tqFontId.fontId) : undefined;
        const fonts: TQFont[] = [];
        if (fontIfExists == undefined && tqFontId.postscriptName && tqFontId.postscriptName != "") {
            fontIfExists = this._collabFonts.get(tqFontId.postscriptName);
        }
        if (fontIfExists) {
            familyName = fontIfExists.familyName;
        }

        if (familyName != "") {
            for (const [, cachedFont] of this._collabFonts) {
                if (cachedFont.familyName == familyName) {
                    fonts.push(cachedFont);
                }
            }
        }
        return fonts;
    }

    private checkVariableFontInCachedFonts(postscriptName: string) {
        try {
            const cachedFontData = this._cachedFonts.get(postscriptName);
            if (cachedFontData) {
                return cachedFontData.clone();
            }
            if (postscriptName.indexOf("_") != -1 && postscriptName.length > postscriptName.indexOf("_") + 3) {
                const customAxesValue = parseInt(postscriptName.substring(postscriptName.indexOf("_") + 1, postscriptName.indexOf("_") + 2));
                if (customAxesValue != NaN) {
                    // We found a valid custom OTV name
                    const varPrefixValue = postscriptName.substring(0, postscriptName.indexOf("_"));
                    const filtered = [...this._cachedFonts.entries()].filter(it => it[1].isVariableFont && it[1].variableFamilyPrefix == varPrefixValue);
                    if (filtered.length) {
                        const retFont: TQFont = filtered[0][1].clone();
                        retFont.postscriptName = postscriptName;
                        retFont.varAxisValues = TQUtils.getAxisValuesFromPostscriptName(postscriptName);
                        return retFont;
                    }
                }
            }
        } catch (e) {
            // ..
        }
    }

    public async getFontFromLocalInfo(postscriptName: string, tkId = "", type: tqtypes.FontType = tqtypes.FontType.DEFAULT): Promise<TQFont> {
        for (const scheduler of TQTaskScheduler._schedulersList) {
            if (tkId && tkId != "") {
                const cachedFontData = scheduler._cachedFonts.get(postscriptName);
                if (cachedFontData) {
                    return cachedFontData.clone();
                }
            }
            const cachedFontData = scheduler._cachedFonts.get(tkId);
            if (cachedFontData) {
                return cachedFontData.clone();
            }
        }

        let fontJSON: any[] = [];
        if (tkId != "") {
            fontJSON = await this.dbWrapper.dbQueryUp([tkId], ["tkId"], BrowseTables.BROWSE, true);
            if (fontJSON == undefined || fontJSON.length == 0) {
                // try by variation id
                const varId = tkId.split("-").slice(0, 2).pop();
                if (varId && varId != "") {
                    fontJSON = await this.dbWrapper.dbQueryUp([varId], ["variationId"], BrowseTables.BROWSE, true);
                }
            }
        } else if (postscriptName != "") {
            fontJSON = await this.dbWrapper.dbQueryUp([postscriptName], ["psName"], BrowseTables.BROWSE, true);
        }

        if (fontJSON == undefined || fontJSON.length == 0) {
            // Check is matches any variable font name
            const fontIfFound = this.checkVariableFontInCachedFonts(postscriptName);
            if (fontIfFound) {
                return fontIfFound;
            }
        }

        if (fontJSON && fontJSON.length) {
            const font = TQUtils.createFontFromDBData(fontJSON[0], type);
            for (const scheduler of TQTaskScheduler._schedulersList) {
                if (scheduler.cachedFonts().has(font.postscriptName)) {
                    font.isActiveForUser = true;
                }
                return font;
            }
        }

        let fontIdentifier: any = undefined;
        if (tkId && tkId != "") {
            fontIdentifier = tkId;
        } else if (postscriptName && postscriptName != "") {
            fontIdentifier = postscriptName;
        }
        if (fontIdentifier) {
            const byofFontsData = this._cachedFonts.get(fontIdentifier);
            if (byofFontsData) {
                return byofFontsData.clone();
            }
        }

        const accessToken = this._initParams.authParams.accessToken;
        if (this._byofFontsLastCheck == 0 && TQUtils.isGivenScopePresent(accessToken, "af_byof") && this._obtainedTokenParams.entitlement == tqtypes.Entitlement.FULL) {
            // not checked yet, on start case possibly
            const callback: tqtypes.GenericContext<tqtypes.AggregationContext> = {
                root: {
                    page: 1,
                    perPage: 100,
                    isRequested: false,
                },
                retryCount: 2,
            };

            const response = {
                aggregation: new TQFontAggregation(),
                error: new tqtypes.Error(),
            };
            (await this.scheduleTask(tqtypes.TaskType.BYOF_COUNT, callback)) as Promise<typeof response>;
            const byofFontsData = this._cachedFonts.get(fontIdentifier);
            if (byofFontsData) {
                return byofFontsData.clone();
            }
        }

        if ([...this._cachedFonts].filter(entry => entry[1].type == tqtypes.FontType.APPLICATION).length == 0 && this.initParams.applicationFontsCollectionType) {
            // We don't have any custom fonts yet
            await this.fetchAndprocessCustomCatalog();
        }

        // Last attempt
        const fontIfFound = this.checkVariableFontInCachedFonts(fontIdentifier);
        if (fontIfFound) {
            return fontIfFound;
        }
        return new TQFont();
    }

    public async getFontFromLocalOrRemoteInfo(postscriptName: string, tkId = "", type: tqtypes.FontType = tqtypes.FontType.DEFAULT) {
        if (postscriptName == "" && (tkId == "" || tkId.substring(0, 4) != "TkD-")) {
            return undefined;
        }
        let fontIfExists = await this.getFontFromLocalInfo(postscriptName, tkId, type);
        if (fontIfExists && fontIfExists.fontId != "") {
            return fontIfExists;
        }
        if ([...this._cachedFonts].filter(entry => entry[1].type == tqtypes.FontType.APPLICATION).length == 0 && this.initParams.applicationFontsCollectionType) {
            // We don't have any custom fonts yet
            await this.fetchAndprocessCustomCatalog();
        }

        // Last attempt
        fontIfExists = await this.getFontFromLocalInfo(postscriptName, tkId, type);
        if (fontIfExists && fontIfExists.fontId != "") {
            return fontIfExists;
        }

        const cb: any = null;
        const callbacks: tqtypes.GenericContext<tqtypes.InternalContext> = {
            root: {
                downloadCB: cb as tqtypes.FontDownloadCallback,
                fontsList: postscriptName != "" ? [postscriptName] : [],
                fontsIds: tkId != "" ? [tkId] : [],
                unicodesList: [],
                fontInfo: new TQFont(type),
            },
            retryCount: 1,
        };

        if ((await this.fetchCachedEntitlement()) == tqtypes.Entitlement.ANONYMOUS) {
            return undefined; // no go for service search
        }
        const fonts: TQFont[] = await this.scheduleTask(tqtypes.TaskType.FONT_SEARCH, callbacks);
        return fonts.length && fonts[0].postscriptName == "" ? undefined : fonts[0];
    }

    private _processSearchInternalResponse = async (response: TQHttpResponse, _callback: any) => {
        if (response.status != tqconstants.ResponseStatus.OK) {
            if (response.taskType == tqtypes.TaskType.FONT_SEARCH_INTERNAL) {
                const errCode = TQUtils.mapStatusNumberToErrorCode(response.status);
                const callback = _callback as tqtypes.GenericContext<tqtypes.InternalContext>;
                for (const fontIndex in callback.root.fontsList) {
                    const singlefontInfo: TQFont = new TQFont(tqtypes.FontType.DEFAULT, callback.root.fontsList[fontIndex], "");
                    const dispatchCallbackFn = async () => {
                        callback.root.downloadCB(singlefontInfo, new tqtypes.Error(errCode, "Searching Error", true));
                    };
                    dispatchCallbackFn();
                }
            }
            // No need to handle offline usage report
        } else if (response.taskType == tqtypes.TaskType.FONT_SEARCH_INTERNAL) {
            // Regular search
            const fontInfos: JSON[] = response.body["results"];
            const callback = _callback as tqtypes.GenericContext<tqtypes.InternalContext>;

            for (const fontJSON of fontInfos) {
                const font = TQUtils.createFontFromFindFontResult(fontJSON, tqtypes.FontType.DEFAULT);

                if (font.fontId != "") {
                    // schedule download
                    const callbacks: tqtypes.GenericContext<tqtypes.InternalContext> = {
                        root: {
                            downloadCB: callback.root.downloadCB,
                            fontsList: [font.fontId],
                            fontsIds: [],
                            fontInfo: font,
                            unicodesList: callback.root.unicodesList,
                        },
                        retryCount: callback.retryCount,
                    };
                    this.scheduleTask(tqtypes.TaskType.FONT_DOWNLOAD, callbacks);
                } else {
                    const dispatchCallbackFn = async () => {
                        callback.root.downloadCB(font, new tqtypes.Error(tqtypes.ErrorCode.FONT_NOT_FOUND, "Font not found"));
                    };
                    dispatchCallbackFn();
                }
            }
        }
        return true;
    };

    private _processSearchResponse = async (response: TQHttpResponse, _callback: any) => {
        const callback = _callback as tqtypes.GenericContext<tqtypes.InternalContext>;
        const dispatchCallbackFn = async (font: TQFont, error: tqtypes.Error) => {
            if (callback.root.downloadCB) {
                callback.root.downloadCB(font, error);
                return true;
            }
        };

        const fonts: TQFont[] = [];
        if (response.status != tqconstants.ResponseStatus.OK) {
            const errCode = TQUtils.mapStatusNumberToErrorCode(response.status);
            for (const fontIndex in callback.root.fontsList) {
                const singlefontInfo: TQFont = new TQFont(tqtypes.FontType.DEFAULT, callback.root.fontsList[fontIndex], "");
                fonts.push(singlefontInfo);
                dispatchCallbackFn(singlefontInfo, new tqtypes.Error(errCode, "Searching Error", true));
            }
        } else {
            // Regular search
            const fontInfos: any[] = response.body["results"];
            for (const fontInfo of fontInfos) {
                const font = TQUtils.createFontFromFindFontResult(fontInfo, tqtypes.FontType.DEFAULT);
                let err = new tqtypes.Error();
                if (font.fontId == "" || font.postscriptName == "") {
                    err = new tqtypes.Error(tqtypes.ErrorCode.FONT_NOT_FOUND, "Font not found");
                }
                fonts.push(font);
                dispatchCallbackFn(font, err);
            }
        }
        return fonts; // return non notified fonts;
    };

    private _processFileResponse = (response: TQHttpResponse, _callback: any) => {
        let err: tqtypes.Error = new tqtypes.Error();
        if (response.status != tqconstants.ResponseStatus.OK) {
        } else {
            const errCode = TQUtils.mapStatusNumberToErrorCode(response.status);
            err = new tqtypes.Error(errCode, "Error occurred");
        }
        const lastModTime: string = this.getResponseHeader(response, "last-modified");
        return { file: response.body, err: err, lastModTime: lastModTime };
    };

    private _processFontCatalogUrl(response: TQHttpResponse, _callback: any) {
        if (response.status != tqconstants.ResponseStatus.OK) {
            // ...
            this.catalogStatus = CatalogStatus.UNINITIALIZED; // mark uninit first
        } else {
            // ...
            this._obtainedTokenParams.catalog = response.body["links"]["catalog"] as string;
        }
    }

    private _processRecommendationUrl(response: TQHttpResponse, _callback: any) {
        if (response.status != tqconstants.ResponseStatus.OK) {
            // ...
        } else {
            // ...
            // const urlJson = response.body["families"];
            // this._recommendedUrlsMap.set(tqtypes.FontType.RECOMMENDED, urlJson["for_you"]);
            // this._recommendedUrlsMap.set(tqtypes.FontType.NEWEST, urlJson["newest"]);
            // this._recommendedUrlsMap.set(tqtypes.FontType.TRNENDING, urlJson["trending"]);
            // urlMap.set(tqtypes.FontType.HIDDEN_GEMS, urlJson["hidden_gems"]);
            // urlMap.set(tqtypes.FontType.STAFF_CHOICE, urlJson["staff_choice"]);
        }
    }

    private async _processMoreSlugs(response: TQHttpResponse, _callback: any) {
        if (response.status != tqconstants.ResponseStatus.OK) {
            // ...
        } else {
            // ...
            const slugs: string[] = response.body;
            const res = await this._fetchDefaultFamiliesData(slugs, false);
            return res;
        }
    }

    private constructLangNamesMap(responseBody: any) {
        const langsMap = new Map<tqtypes.Language, string>();
        const langs: any[] = responseBody["languages"];
        if (langs) {
            for (const lang of langs) {
                const region = TQUtils.getLanguageFromString(lang["key"]);
                if (true == Object.values(tqtypes.Language).includes(region) && lang["label"]) {
                    langsMap.set(region, lang["label"]);
                }
            }
        }
        return langsMap;
    }

    public async getLangNames() {
        let langMap: Map<tqtypes.Language, string> = new Map<tqtypes.Language, string>();
        const region: string = await TQUtils.getAppropriateRegion();
        const langMapJson = await this._responseCache.getValue(CacheResponseType.LANG_FILTER_DUMMY_ENDPOINT_PREFIX + region + CacheResponseType.LANG_FILTER_DUMMY_ENDPOINT_SUFFIX);
        if (langMapJson && langMapJson.languages && langMapJson.languages.length > 0) {
            langMap = this.constructLangNamesMap(langMapJson);
        } else {
            langMap = await this.scheduleTask(tqtypes.TaskType.FETCH_LANGS, null);
        }
        const err = new tqtypes.Error();
        if (langMap.size == 0) {
            err.errCode = tqtypes.ErrorCode.UNKNOWN;
        }
        return { languageMap: langMap, error: err };
    }

    private async _processLangNames(res: TQHttpResponse, _callback: any) {
        if (res.status != tqconstants.ResponseStatus.OK) {
            // ...
        } else {
            // ...
            const region: string = await TQUtils.getAppropriateRegion();
            this._responseCache.putValue(CacheResponseType.LANG_FILTER_DUMMY_ENDPOINT_PREFIX + region + CacheResponseType.LANG_FILTER_DUMMY_ENDPOINT_SUFFIX, res.body);
            return this.constructLangNamesMap(res.body);
        }
    }

    private async _processByofUploadUrl(res: TQHttpResponse, _callback: any): Promise<{ status: boolean; error: tqtypes.Error }> {
        if (TQUtils.mapStatusNumberToErrorCode(res.status) != tqtypes.ErrorCode.NONE) {
            // ...
            const errCode = TQUtils.mapStatusNumberToErrorCode(res.status);
            const err = new tqtypes.Error(errCode, "BYOF upload Error", false);
            if (err.errCode == tqtypes.ErrorCode.AUTH_FAILURE) {
                this.handleAuthFailure();
            }
            return { status: false, error: err };
        } else {
            // ...
            const uploadUrl = res.body["upload_url"];
            if (uploadUrl && uploadUrl != "") {
                const context = _callback as tqtypes.GenericContext<tqtypes.UploadContext>;
                return (await this.scheduleTask(tqtypes.TaskType.FILE_UPLOAD, _callback, uploadUrl)) as { status: boolean; error: tqtypes.Error };
            } else {
                return { status: false, error: new tqtypes.Error(tqtypes.ErrorCode.UNKNOWN, "Unknown error ") };
            }
        }
    }

    private async _handleByofAccessLevelResponse(req: TQHttpRequest, res: TQHttpResponse, _callback: any) {
        const errCode = TQUtils.mapStatusNumberToErrorCode(res.status);
        const err = new tqtypes.Error(errCode, "BYOF access check", false);
        let status = true; // Assume user can upload
        if (errCode != tqtypes.ErrorCode.NONE) {
            if (errCode != tqtypes.ErrorCode.SERVER_UNREACHABLE && errCode != tqtypes.ErrorCode.OFFLINE) {
                status = false;
            }
        } else {
            // ...
            if (res.body && res.body["access_level"] && res.body["access_level"]["upload"] == false) {
                status = false;
            }
        }
        const headers = req.headers.filter(r => r[0] == tqconstants.HeaderKey.AUTHORIZARION);
        if (headers.length && headers[0][1]) {
            const accessToken = headers[0][1].replace(tqconstants.HeaderValue.BEARER, "");
            this._accessTokenByofStatus.set(accessToken, status);
            const cachedUserAccessLevelKey = CacheResponseType.BYOF_ACCESS_ENDPOINT_PREFIX + this._initParams.authParams.userId + CacheResponseType.BYOF_ACCESS_ENDPOINT_SUFFIX;
            this._responseCache.putValue(cachedUserAccessLevelKey, status);
        }
        return { error: err, hasAccess: status };
    }

    private async _handleByofAuthDeleteResponse(req: TQHttpRequest, res: TQHttpResponse, _callback: any) {
        const errCode = TQUtils.mapStatusNumberToErrorCode(res.status);
        const err = new tqtypes.Error(errCode);
        const status = errCode == tqtypes.ErrorCode.NONE ? true : false;
        return { error: err, status: status };
    }

    private async _handleAnsShortPollResponse(res: TQHttpResponse, _callback: any) {
        if (TQUtils.mapStatusNumberToErrorCode(res.status) != tqtypes.ErrorCode.NONE) {
            const errCode = TQUtils.mapStatusNumberToErrorCode(res.status);
            const err = new tqtypes.Error(errCode, "Received error from ANS", false);
            return { status: false, error: err };
        } else {
            // ...
            const ret = res.body["notifications"];
            for (const notification of ret["notification"]) {
                if (notification["type"] == "com.adobe.fonts.v1" && notification["sub-type"] == "byof_upload_status" && notification["payload"]) {
                    // Let's see what our status in the payload is
                    const payload = notification["payload"];
                    if (payload["status"] == "failed") {
                        // Failed, get the error.
                    } else if (payload["status"] == "processed") {
                        // Passed, Fire a poll & immediately update
                    }
                }
            }
            return { status: true, error: new tqtypes.Error() };
        }
    }

    private async _processFileUploadResponse(res: TQHttpResponse, _callback: any) {
        if (TQUtils.mapStatusNumberToErrorCode(res.status) != tqtypes.ErrorCode.NONE) {
            const errCode = TQUtils.mapStatusNumberToErrorCode(res.status);
            const err = new tqtypes.Error(errCode, "File upload Error to the endpoint", false);
            return { status: false, error: err };
        } else {
            // ...
            return { status: true, error: new tqtypes.Error() };
        }
    }

    private async _processCollabAuthResponse(response: TQHttpResponse, _callback: any) {
        return { responsebody: response.body, err: new tqtypes.Error(TQUtils.mapStatusNumberToErrorCode(response.status)), callback: _callback };
    }

    private _processResponse = (request: TQHttpRequest, response: TQHttpResponse, _callback: any) => {
        switch (response.taskType) {
            case tqtypes.TaskType.TOKEN_FETCH:
            case tqtypes.TaskType.TOKEN_FETCH_INTERNAL:
                // code block
                return this._processTokenResponse(response, _callback);

            case tqtypes.TaskType.FONT_SEARCH:
                return this._processSearchResponse(response, _callback);

            case tqtypes.TaskType.FONT_SEARCH_INTERNAL:
                // code block
                return this._processSearchInternalResponse(response, _callback);

            case tqtypes.TaskType.FONT_DOWNLOAD:
                return this._processFontDownloadResponse(request, response, _callback);

            case tqtypes.TaskType.USAGE_REPORT:
                return this._processUseFontReportResponse(response, _callback);

            case tqtypes.TaskType.FILE_DOWNLOAD:
                return this._processFileResponse(response, _callback);

            case tqtypes.TaskType.ACTIVATED_FONTS_COUNT:
                return this._activatedCountResponse(response, _callback);

            case tqtypes.TaskType.ACTIVATED_FONTS:
                return this._activatedResponse(response, _callback);

            case tqtypes.TaskType.BYOF_COUNT:
                return this._byofCountResponse(response, _callback);

            case tqtypes.TaskType.BYOF_LISTS:
                return this._byofListResponse(response, _callback);

            case tqtypes.TaskType.ACTIVATED_FONTS_POLL:
                return this._activatedPollResponse(response, _callback);

            case tqtypes.TaskType.BYOF_FONTS_POLL:
                return this._byofPollResponse(response, _callback);

            case tqtypes.TaskType.FONT_CATALOG_URL:
                return this._processFontCatalogUrl(response, _callback);

            case tqtypes.TaskType.FETCH_FAMILY:
                return this._fetchFamilyResponse(response, _callback);

            case tqtypes.TaskType.SEARCH_FAMILY:
                return this._searchFamilyResponse(response, _callback);

            case tqtypes.TaskType.CATALOG_DOWNLOAD:
                return this._handleCatalogResponse(response, _callback);

            case tqtypes.TaskType.CUSTOM_CATALOG_URL_VARIABLE:
            case tqtypes.TaskType.CUSTOM_CATALOG_URL_PHOTOSHOP:
                return this._handleCustomBrowseCatalogUrlResponse(response, _callback);

            case tqtypes.TaskType.CUSTOM_CATALOG_DOWNLOAD:
                return this._handleCustomBrowseCatalogResponse(response, _callback);

            case tqtypes.TaskType.TAG_CATALOG_URL:
                return this._processTagCatalogUrl(response, _callback);

            case tqtypes.TaskType.FETCH_LANGS:
                return this._processLangNames(response, _callback);

            case tqtypes.TaskType.RECOMMENDATION_URL:
                return this._processRecommendationUrl(response, _callback);

            case tqtypes.TaskType.BYOF_UPLOAD_URL:
                return this._processByofUploadUrl(response, _callback);

            case tqtypes.TaskType.BYOF_ACCESS_LEVEL:
                return this._handleByofAccessLevelResponse(request, response, _callback);

            case tqtypes.TaskType.BYOF_DELETE:
                return this._handleByofAuthDeleteResponse(request, response, _callback);

            case tqtypes.TaskType.FILE_UPLOAD:
                return this._processFileUploadResponse(response, _callback);

            case tqtypes.TaskType.ANS_SHORT_POLL:
                return this._handleAnsShortPollResponse(response, _callback);

            case tqtypes.TaskType.RECOMMENDED_SLUGS:
            case tqtypes.TaskType.NEWEST_SLUGS:
            case tqtypes.TaskType.TRENDING_SLUGS:
                return this._processMoreSlugs(response, _callback);

            case tqtypes.TaskType.COLLAB_AUTH_API:
                return this._processCollabAuthResponse(response, _callback);

            default:
            // code block
        }
    };

    public async scheduleTask(taskType: tqtypes.TaskType, tqContext: any, actionUrl = "", lastModTime = "", customHeaders: [tqconstants.HeaderKey, string][] = []) {
        const req = new TQHttpRequest(taskType);

        const blockedTokenResponse: TQHttpResponse = new TQHttpResponse(req.taskType);
        blockedTokenResponse.status = tqconstants.ResponseStatus.UNAUTHORIZED;

        switch (req.taskType) {
            case tqtypes.TaskType.TOKEN_FETCH:
            case tqtypes.TaskType.TOKEN_FETCH_INTERNAL:
                {
                    if (this._accessTokenBlockedSet.has(this._initParams.authParams.accessToken)) {
                        return this._processResponse(req, blockedTokenResponse, tqContext);
                    }

                    this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.TYPEKIT_STAGE) : (req.url = tqconstants.ServerUrl.TYPEKIT_PROD);
                    req.url += tqconstants.ServerAPI.TYPEKIT_TOKEN;
                    const headers: [tqconstants.HeaderKey, string][] = [
                        [tqconstants.HeaderKey.CONTENT_TYPE, tqconstants.HeaderValue.ACCEPT_JSON],
                        [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                    ];

                    if (TQUtils.isValidAccessToken(this.initParams.authParams.accessToken) == false) {
                        headers.push([tqconstants.HeaderKey.SESSION_ID, this._initParams.authParams.userId]);
                    } else {
                        headers.push([tqconstants.HeaderKey.AUTHORIZARION, tqconstants.HeaderValue.BEARER + this._initParams.authParams.accessToken]);
                    }
                    const locale: string = await TQUtils.getAppropriateRegion();
                    req.url += "?" + tqconstants.ServerQueryParams.LOCALE_KEY + locale;
                    req.url += "&" + tqconstants.ServerQueryParams.VERSION_KEY + "2.2.0";
                    req.url += "&" + tqconstants.HeaderKey.X_REQUEST_ID + "=" + this.initParams.runtimeMode + "_" + new Date().getTime();

                    req.method = tqconstants.RequestMethod.GET;
                    req.headers = headers;
                }
                break;

            case tqtypes.TaskType.FONT_SEARCH:
            case tqtypes.TaskType.FONT_SEARCH_INTERNAL:
                {
                    // Adobe Fonts
                    this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.TYPEKIT_STAGE) : (req.url = tqconstants.ServerUrl.TYPEKIT_PROD);

                    req.url += tqconstants.ServerAPI.TYPEKIT_SEARCH;
                    const headers: [tqconstants.HeaderKey, string][] = [
                        [tqconstants.HeaderKey.CONTENT_TYPE, tqconstants.HeaderValue.ACCEPT_JSON],
                        [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                    ];

                    // No check entitlement needed in case of usage report
                    const checkEntitlement = true;
                    headers.push([tqconstants.HeaderKey.AUTHORIZARION, tqconstants.HeaderValue.BEARER + this._initParams.authParams.accessToken]);

                    req.method = tqconstants.RequestMethod.POST;
                    req.headers = headers;

                    const locale: string = TQUtils.getAppropriateLocale();
                    req.url = req.url.concat("?" + tqconstants.ServerQueryParams.LOCALE_KEY + locale);

                    const reqBody = {
                        auto_activate: false,
                        postscript_names: [""],
                        tk_font_ids: [""],
                        check_entitlement: checkEntitlement,
                    };
                    const tqContextReceived = tqContext as tqtypes.GenericContext<tqtypes.InternalContext>;
                    reqBody["postscript_names"] = tqContextReceived.root.fontsList;
                    reqBody["tk_font_ids"] = tqContextReceived.root.fontsIds;
                    req.body = JSON.stringify(reqBody);
                }
                break;

            case tqtypes.TaskType.FONT_DOWNLOAD:
                {
                    const fontBufferEndPoint = (function (initParams: any) {
                        switch (initParams.fontBufferType) {
                            case tqtypes.FontBufferType.RAW:
                                return tqconstants.ServerAPI.USEFONT_DOWNLOAD_SUFFIX_RAW;
                            case tqtypes.FontBufferType.WOFF:
                                return tqconstants.ServerAPI.USEFONT_DOWNLOAD_SUFFIX_WOFF;
                            case tqtypes.FontBufferType.WOFF2:
                                return tqconstants.ServerAPI.USEFONT_DOWNLOAD_SUFFIX_WOFF2;
                            default:
                                return tqconstants.ServerAPI.USEFONT_DOWNLOAD_SUFFIX_RAW_PROTECTED;
                        }
                    })(this.initParams);

                    const tqContextReceived = tqContext as tqtypes.GenericContext<tqtypes.InternalContext>;
                    tqContext = tqContextReceived;
                    if (actionUrl == "" && this._obtainedTokenParams.expiry < Math.round(new Date().getTime() / 1000)) {
                        // expiry is less than current
                        this.scheduleTask(tqtypes.TaskType.TOKEN_FETCH_INTERNAL, tqContext);
                        return;
                    } else {
                        const headers: [tqconstants.HeaderKey, string][] = [[tqconstants.HeaderKey.ACCEPT, tqconstants.HeaderValue.ALL]];

                        if (this._cachedFonts.has(tqContextReceived.root.fontInfo.postscriptName) && TQTaskScheduler._cachedFontsUrlProd.has(tqContextReceived.root.fontInfo.postscriptName)) {
                            let downloadUrl: string | undefined = "";
                            if (this._initParams.authParams.isStaging) {
                                downloadUrl = TQTaskScheduler._cachedFontsUrlStage.get(tqContextReceived.root.fontInfo.postscriptName);
                            } else {
                                downloadUrl = TQTaskScheduler._cachedFontsUrlProd.get(tqContextReceived.root.fontInfo.postscriptName);
                            }

                            if (downloadUrl) {
                                req.url = downloadUrl;
                            }
                            req.isBlob = true;
                        } else if (actionUrl == "" && tqContextReceived.root.fontInfo.type == tqtypes.FontType.BYOF) {
                            // ..
                            this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.BYOF_STAGE) : (req.url = tqconstants.ServerUrl.BYOF_PROD);
                            req.url += tqconstants.ServerAPI.BYOF_DOWNLOAD + "?checksums=" + tqContextReceived.root.fontInfo.fontId;
                            headers.push([tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId]);
                            headers.push([tqconstants.HeaderKey.AUTHORIZARION, tqconstants.HeaderValue.BEARER + this._initParams.authParams.accessToken]);
                        } else if (actionUrl == "" && tqContextReceived.root.fontInfo.type == tqtypes.FontType.APPLICATION) {
                            // ..
                            if (this.initParams.applicationFontsCollectionType && tqContextReceived.root.fontInfo._subtype) {
                                let customParams = this.obtainedTokenParams.mapOfCustomCatalogParams.get(tqContextReceived.root.fontInfo._subtype);
                                if (customParams) {
                                    if (customParams.expiry < Math.round(new Date().getTime() / 1000)) {
                                        await this.fetchCustomCatalogUrl();
                                        const ret = this.obtainedTokenParams.mapOfCustomCatalogParams.get(tqContextReceived.root.fontInfo._subtype);
                                        if (ret) {
                                            customParams = ret;
                                        }
                                    }
                                    req.url = customParams.genericdownloadUrl;
                                    req.url += tqContextReceived.root.fontInfo.fontId;
                                    req.url += "?__token__=" + customParams.token;
                                }
                                req.isBlob = true;
                            }
                            //headers.push([tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId]);
                            //headers.push([tqconstants.HeaderKey.AUTHORIZARION, tqconstants.HeaderValue.BEARER + this._initParams.authParams.accessToken]);
                        } else if (actionUrl == "" && tqContextReceived.root.fontsList.length > 0) {
                            headers.push([tqconstants.HeaderKey.SUBSET_SERVICE_AUTHORIZATION, this._obtainedTokenParams.token]);
                            this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.USE_FONTS_STAGE) : (req.url = tqconstants.ServerUrl.USE_FONTS_PROD);
                            if (this._obtainedTokenParams.entitlement == tqtypes.Entitlement.ANONYMOUS) {
                                req.url += "/ps";
                            } else {
                                req.url += tqconstants.ServerAPI.USEFONT_DOWNLOAD_PREFIX + "full"; // this._obtainedTokenParams.entitlement;
                            }
                            req.url += fontBufferEndPoint + tqContextReceived.root.fontsList[0];
                            req.url += "?unicode=";
                            if (tqContextReceived.root.unicodesList.length) {
                                const size = BloomFilter.calcSize(tqContextReceived.root.unicodesList.length, 0.05);
                                let hashes = BloomFilter.calcHashes(tqContextReceived.root.unicodesList.length, size);
                                if (tqContextReceived.root.unicodesList.length > 50) {
                                    hashes = Math.min(10, hashes * Math.floor(tqContextReceived.root.unicodesList.length / 50));
                                }
                                const filter = new BloomFilter(size, hashes);
                                for (const unicode of tqContextReceived.root.unicodesList) {
                                    if (false == filter.has(unicode)) {
                                        filter.add(unicode);
                                    }
                                }
                                if (filter.toBase64().length > 2000) {
                                    req.url += "AAAAAQAAAAEAAAAB"; // Full font for all browser compatiability
                                } else {
                                    req.url += filter.toBase64();
                                }
                            } else {
                                req.url += "AAAAAQAAAAEAAAAB"; // Full font
                            }
                            req.isBlob = true;
                        } else {
                            // ...
                            req.url = actionUrl;
                            req.headers = customHeaders;
                            // headers.push([tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId]);
                            // headers.push([tqconstants.HeaderKey.AUTHORIZARION, tqconstants.HeaderValue.BEARER + this._initParams.authParams.accessToken]);
                            req.isBlob = true;
                        }

                        req.method = tqconstants.RequestMethod.GET;
                        req.headers.push(...headers);
                    }
                }
                break;

            case tqtypes.TaskType.USAGE_REPORT:
                {
                    const context = tqContext as tqtypes.GenericContext<tqtypes.UsageInternalContext>;
                    this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.TYPEKIT_STAGE) : (req.url = tqconstants.ServerUrl.TYPEKIT_PROD);

                    if (context.root.fontsList.length > 0) {
                        req.url += tqconstants.ServerAPI.USEFONT_USAGE;

                        const headers: [tqconstants.HeaderKey, string][] = [
                            [tqconstants.HeaderKey.CONTENT_TYPE, tqconstants.HeaderValue.ACCEPT_JSON],
                            [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                        ];

                        if (TQUtils.isValidAccessToken(this._initParams.authParams.accessToken)) {
                            headers.push([tqconstants.HeaderKey.AUTHORIZARION, tqconstants.HeaderValue.BEARER + this._initParams.authParams.accessToken]);
                        } else {
                            headers.push([tqconstants.HeaderKey.SESSION_ID, this._initParams.authParams.userId]);
                        }

                        req.method = tqconstants.RequestMethod.POST;
                        req.headers = headers;

                        const docId = await TQUtils.convertToSha256(context.root.documentId);

                        let reqBody: JSON[] = [];
                        for (const index in context.root.fontsList) {
                            const singleFontData: any = {
                                sync_font_id: context.root.fontsList[index],
                                document_id: docId,
                                app_id: this._initParams.authParams.appId,
                                app_version: this._initParams.authParams.appVersion,
                                used_at: Math.round(new Date().getTime() / 1000),
                                operation: context.root.operation,
                                activate: context.root.autoActivate,
                                install_state: context.root.installState.toString(),
                                readonly: context.root.readonly,
                            };
                            reqBody = reqBody.concat(singleFontData);
                        }
                        req.body = JSON.stringify(reqBody);
                    } else {
                        // return true;
                    }
                }
                break;

            case tqtypes.TaskType.FILE_DOWNLOAD:
                {
                    req.url = actionUrl;
                    req.method = tqconstants.RequestMethod.GET;
                    req.isBlob = true;

                    if (lastModTime.length) {
                        const headers: [tqconstants.HeaderKey, string][] = [[tqconstants.HeaderKey.IF_MODIFIED_SINCE, lastModTime]];
                        req.headers = headers;
                    }
                }
                break;

            case tqtypes.TaskType.FILE_UPLOAD:
                {
                    req.url = actionUrl;
                    req.method = tqconstants.RequestMethod.PUT;
                    req.isBlob = true;

                    const headers: [tqconstants.HeaderKey, string][] = [[tqconstants.HeaderKey.CONTENT_TYPE, tqconstants.HeaderValue.MULTIPART_FORM_DATA]];
                    req.headers = headers;
                    const context = tqContext as tqtypes.GenericContext<tqtypes.UploadContext>;
                    req.body = await context.root.file.arrayBuffer();
                }
                break;

            case tqtypes.TaskType.FONT_CATALOG_URL:
                {
                    this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.TYPEKIT_STAGE) : (req.url = tqconstants.ServerUrl.TYPEKIT_PROD);
                    req.url += tqconstants.ServerAPI.TYPEKIT_CATALOG_URL;

                    const headers: [tqconstants.HeaderKey, string][] = [
                        [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                        [tqconstants.HeaderKey.AUTHORIZARION, tqconstants.HeaderValue.BEARER + this._initParams.authParams.accessToken],
                    ];
                    req.headers = headers;

                    // const locale:string = await TQUtils.getAppropriateLocale();
                    req.url = req.url.concat("?" + tqconstants.ServerQueryParams.LIBRARY_KEY + tqconstants.ServerQueryParams.LIBRARY_VALUE_TAGS /* this._obtainedTokenParams.entitlement  */);
                    req.url = req.url.concat("&" + tqconstants.ServerQueryParams.VERSION_KEY + "2.2.0");
                    // req.url = req.url.concat("&" + tqconstants.ServerQueryParams.LOCALE_KEY + locale);
                }
                break;

            case tqtypes.TaskType.CUSTOM_CATALOG_URL_VARIABLE:
            case tqtypes.TaskType.CUSTOM_CATALOG_URL_PHOTOSHOP:
                {
                    if (this._accessTokenBlockedSet.has(this._initParams.authParams.accessToken)) {
                        return this._processResponse(req, blockedTokenResponse, tqContext);
                    }
                    this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.TYPEKIT_STAGE) : (req.url = tqconstants.ServerUrl.TYPEKIT_PROD);
                    req.url += tqconstants.ServerAPI.TYPEKIT_TOKEN;
                    const headers: [tqconstants.HeaderKey, string][] = [
                        [tqconstants.HeaderKey.CONTENT_TYPE, tqconstants.HeaderValue.ACCEPT_JSON],
                        [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                        [tqconstants.HeaderKey.AUTHORIZARION, tqconstants.HeaderValue.BEARER + this._initParams.authParams.accessToken],
                    ];

                    const customCatalogEndPoint = (function (taskType: tqtypes.TaskType) {
                        switch (taskType) {
                            case tqtypes.TaskType.CUSTOM_CATALOG_URL_PHOTOSHOP:
                                return tqtypes.ApplicationFontsType.PHOTOSHOP;
                            default:
                                return tqtypes.ApplicationFontsType.VARIABLE;
                        }
                    })(req.taskType);

                    const locale: string = await TQUtils.getAppropriateRegion();
                    req.url += "?" + tqconstants.ServerQueryParams.LOCALE_KEY + locale;
                    req.url += "&" + "l=" + customCatalogEndPoint;
                    req.url += "&" + tqconstants.ServerQueryParams.VERSION_KEY + "1.0.0";
                    req.method = tqconstants.RequestMethod.GET;
                    req.headers = headers;
                }
                break;

            case tqtypes.TaskType.ACTIVATED_FONTS_POLL:
            case tqtypes.TaskType.ACTIVATED_FONTS:
            case tqtypes.TaskType.ACTIVATED_FONTS_COUNT:
            case tqtypes.TaskType.BYOF_COUNT:
            case tqtypes.TaskType.BYOF_LISTS:
            case tqtypes.TaskType.BYOF_FONTS_POLL:
                {
                    if (this._accessTokenBlockedSet.has(this._initParams.authParams.accessToken)) {
                        return this._processResponse(req, blockedTokenResponse, tqContext);
                    }
                    const context = tqContext as tqtypes.GenericContext<tqtypes.AggregationContext>;
                    if (req.taskType == tqtypes.TaskType.BYOF_COUNT || req.taskType == tqtypes.TaskType.BYOF_LISTS || req.taskType == tqtypes.TaskType.BYOF_FONTS_POLL) {
                        this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.BYOF_STAGE) : (req.url = tqconstants.ServerUrl.BYOF_PROD);
                        req.url += tqconstants.ServerAPI.BYOF_LIST;
                    } else {
                        this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.TYPEKIT_STAGE) : (req.url = tqconstants.ServerUrl.TYPEKIT_PROD);
                        req.url += tqconstants.ServerAPI.TYPEKIT_SELECTIONS;
                    }

                    const headers: [tqconstants.HeaderKey, string][] = [
                        [tqconstants.HeaderKey.ACCEPT, tqconstants.HeaderValue.ALL],
                        [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                        [tqconstants.HeaderKey.AUTHORIZARION, tqconstants.HeaderValue.BEARER + this._initParams.authParams.accessToken],
                    ];
                    req.headers = headers;
                    if (taskType == tqtypes.TaskType.ACTIVATED_FONTS_POLL || taskType == tqtypes.TaskType.BYOF_FONTS_POLL) {
                        req.method = tqconstants.RequestMethod.HEAD;
                        req.isBlob = true;
                    } else {
                        req.method = tqconstants.RequestMethod.GET;
                    }

                    const locale: string = TQUtils.getAppropriateRegion();
                    req.url = req.url.concat("?");
                    if (req.taskType != tqtypes.TaskType.BYOF_COUNT && req.taskType != tqtypes.TaskType.BYOF_LISTS) {
                        req.url = req.url.concat(tqconstants.ServerQueryParams.INSTALL_STATE_KEY + tqconstants.ServerQueryParams.INSTALL_STATE_VALUE);
                        req.url = req.url.concat("&");
                    }
                    req.url = req.url.concat(tqconstants.ServerQueryParams.PAGE_ID_KEY + context.root.page);
                    req.url = req.url.concat("&" + tqconstants.ServerQueryParams.PER_PAGE_ID_KEY + context.root.perPage);
                    req.url = req.url.concat("&" + tqconstants.ServerQueryParams.LOCALE_KEY + locale);
                }
                break;

            case tqtypes.TaskType.FETCH_FAMILY:
                {
                    const context = tqContext as tqtypes.GenericContext<tqtypes.FetchFamilyContext>;
                    this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.TYPEKIT_STAGE) : (req.url = tqconstants.ServerUrl.TYPEKIT_PROD);
                    req.url += tqconstants.ServerAPI.TYPEKIT_GET_FAMILIES;

                    const headers: [tqconstants.HeaderKey, string][] = [
                        [tqconstants.HeaderKey.ACCEPT, tqconstants.HeaderValue.ACCEPT_JSON],
                        [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                    ];
                    req.url += context.root.familySlug;
                    req.headers = headers;
                    req.method = tqconstants.RequestMethod.GET;

                    const locale: string = TQUtils.getAppropriateRegion();
                    req.url = req.url.concat("?" + tqconstants.ServerQueryParams.LOCALE_KEY + locale);
                    req.url = req.url.concat("&" + tqconstants.ServerQueryParams.BROWSE_MODE_KEY + tqconstants.ServerQueryParams.BROWSE_MODE_VALUE_ALL);
                }
                break;

            case tqtypes.TaskType.SEARCH_FAMILY:
                {
                    const context = tqContext as tqtypes.GenericContext<tqtypes.FetchFamilyContext>;
                    this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.TYPEKIT_STAGE) : (req.url = tqconstants.ServerUrl.TYPEKIT_PROD);
                    req.url += tqconstants.ServerAPI.TYPEKIT_SEARCH_FAMILIES;

                    const headers: [tqconstants.HeaderKey, string][] = [
                        [tqconstants.HeaderKey.ACCEPT, tqconstants.HeaderValue.ACCEPT_JSON],
                        [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                    ];
                    req.headers = headers;
                    req.method = tqconstants.RequestMethod.GET;

                    const locale: string = await TQUtils.getAppropriateRegion();
                    req.url = req.url.concat("?" + tqconstants.ServerQueryParams.SEARCH_KEY + context.root.familySlug);
                    req.url = req.url.concat("&" + tqconstants.ServerQueryParams.LOCALE_KEY + locale);
                    req.url = req.url.concat("&" + tqconstants.ServerQueryParams.BROWSE_MODE_KEY + tqconstants.ServerQueryParams.BROWSE_MODE_VALUE_ALL);
                }
                break;

            case tqtypes.TaskType.CUSTOM_CATALOG_DOWNLOAD:
            case tqtypes.TaskType.CATALOG_DOWNLOAD:
                {
                    req.url = actionUrl;
                    const headers: [tqconstants.HeaderKey, string][] = [
                        // [tqconstants.HeaderKey.ACCEPT, tqconstants.HeaderValue.ACCEPT_JSON],
                    ];

                    if (lastModTime.length) {
                        headers.push([tqconstants.HeaderKey.IF_MODIFIED_SINCE, lastModTime]);
                    }
                    req.headers = headers;
                    req.method = tqconstants.RequestMethod.GET;
                }
                break;

            case tqtypes.TaskType.TAG_CATALOG_URL:
                {
                    this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.TYPEKIT_STAGE) : (req.url = tqconstants.ServerUrl.TYPEKIT_PROD);
                    req.url += tqconstants.ServerAPI.TYPEKIT_TAG_CATALOGS;

                    const headers: [tqconstants.HeaderKey, string][] = [
                        [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                        [tqconstants.HeaderKey.ACCEPT, tqconstants.HeaderValue.ACCEPT_JSON],
                    ];
                    req.headers = headers;
                    req.method = tqconstants.RequestMethod.GET;

                    const locale: string = await TQUtils.getAppropriateLocale();
                    // This API
                    req.url = req.url.concat("?" + tqconstants.ServerQueryParams.VERSION_KEY + tqconstants.ServerQueryParams.VERSION_VALUE_TAGS);
                    req.url = req.url.concat("&" + tqconstants.ServerQueryParams.LIBRARY_KEY + tqconstants.ServerQueryParams.LIBRARY_VALUE_TAGS);
                    req.url = req.url.concat("&" + tqconstants.ServerQueryParams.CULTURAL_LOCALE_KEY + locale);
                }
                break;

            case tqtypes.TaskType.RECOMMENDATION_URL:
                {
                    this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.TYPEKIT_STAGE) : (req.url = tqconstants.ServerUrl.TYPEKIT_PROD);
                    req.url += tqconstants.ServerAPI.TYPEKIT_RECOMMENDATION_URLS;
                    const headers: [tqconstants.HeaderKey, string][] = [
                        // [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                        [tqconstants.HeaderKey.ACCEPT, tqconstants.HeaderValue.ACCEPT_JSON],
                    ];
                    req.headers = headers;
                    req.method = tqconstants.RequestMethod.GET;
                }
                break;

            case tqtypes.TaskType.BYOF_ACCESS_LEVEL:
                {
                    this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.BYOF_STAGE) : (req.url = tqconstants.ServerUrl.BYOF_PROD);
                    req.url += tqconstants.ServerAPI.BYOF_UPLOAD_ACCESS_CHECK;
                    const headers: [tqconstants.HeaderKey, string][] = [
                        [tqconstants.HeaderKey.ACCEPT, tqconstants.HeaderValue.ALL],
                        [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                        [tqconstants.HeaderKey.AUTHORIZARION, tqconstants.HeaderValue.BEARER + this._initParams.authParams.accessToken],
                    ];
                    req.headers = headers;
                    req.method = tqconstants.RequestMethod.GET;
                }
                break;

            case tqtypes.TaskType.BYOF_DELETE:
                {
                    this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.BYOF_STAGE) : (req.url = tqconstants.ServerUrl.BYOF_PROD);
                    req.url += tqconstants.ServerAPI.BYOF_AUTH_DELETE;

                    const tqContextReceived = tqContext as tqtypes.GenericContext<tqtypes.InternalContext>;
                    tqContextReceived.root.fontsList.join(",");
                    req.url = req.url.concat("?" + tqconstants.ServerQueryParams.CHECKSUMS_KEY + tqContextReceived.root.fontsList.join(","));
                    const headers: [tqconstants.HeaderKey, string][] = [
                        [tqconstants.HeaderKey.ACCEPT, tqconstants.HeaderValue.ALL],
                        [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                        [tqconstants.HeaderKey.AUTHORIZARION, tqconstants.HeaderValue.BEARER + this._initParams.authParams.accessToken],
                    ];
                    req.headers = headers;
                    req.method = tqconstants.RequestMethod.DELETE;
                }
                break;

            case tqtypes.TaskType.ANS_SHORT_POLL:
                {
                    if (this._initParams.authParams.ansAppIdentifier == "") {
                        return;
                    }
                    this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.ANS_STAGE) : (req.url = tqconstants.ServerUrl.ANS_PROD);
                    req.url += tqconstants.ServerAPI.ANS_QUERY_NOTIFICATIONS;
                    const headers: [tqconstants.HeaderKey, string][] = [
                        [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                        [tqconstants.HeaderKey.USER_AUTHORIZARION, tqconstants.HeaderValue.BEARER + this._initParams.authParams.accessToken],
                        [tqconstants.HeaderKey.ANS_APP_ID, this._initParams.authParams.ansAppIdentifier],
                    ];
                    req.headers = headers;
                    req.method = tqconstants.RequestMethod.GET;
                    const locale: string = await TQUtils.getAppropriateLocale();

                    // This API has page_size=10&config_version=13.9&locale=en_US&current_mc_time=1651661179430&from=1651661078708
                    req.url = req.url.concat("?" + tqconstants.ServerQueryParams.PAGE_SIZE_KEY + "100");
                    req.url = req.url.concat("&" + "_type=json");
                    req.url = req.url.concat("&" + tqconstants.ServerQueryParams.CONFIG_VERSION_KEY + "1");
                    req.url = req.url.concat("&" + tqconstants.ServerQueryParams.LOCALE_KEY + locale);
                    req.url = req.url.concat("&" + tqconstants.ServerQueryParams.CURRENT_TIMESTAMP_KEY + +(new Date().getTime() - 10000)); // 10 secs before
                    req.url = req.url.concat("&" + tqconstants.ServerQueryParams.FUTURE_TIMESTAMP_KEY + +(new Date().getTime() + 120000)); // 60 secs after
                }
                break;

            case tqtypes.TaskType.RECOMMENDED_SLUGS:
            case tqtypes.TaskType.TRENDING_SLUGS:
            case tqtypes.TaskType.NEWEST_SLUGS:
                {
                    req.url = actionUrl;
                    const headers: [tqconstants.HeaderKey, string][] = [
                        [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                        [tqconstants.HeaderKey.ACCEPT, tqconstants.HeaderValue.ACCEPT_JSON],
                        [tqconstants.HeaderKey.AUTHORIZARION, tqconstants.HeaderValue.BEARER + this._initParams.authParams.accessToken],
                    ];
                    req.headers = headers;
                    req.method = tqconstants.RequestMethod.GET;

                    const locale: string = await TQUtils.getAppropriateLocale();
                    // This API
                    req.url = req.url.concat("?" + tqconstants.ServerQueryParams.LIBRARY_KEY + tqconstants.ServerQueryParams.LIBRARY_VALUE_TAGS);
                    req.url = req.url.concat("&" + tqconstants.ServerQueryParams.LOCALE_KEY + locale);
                }
                break;

            case tqtypes.TaskType.FETCH_LANGS:
                {
                    this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.TYPEKIT_STAGE) : (req.url = tqconstants.ServerUrl.TYPEKIT_PROD);
                    req.url += tqconstants.ServerAPI.TYPEKIT_LANG_NAMES;
                    const headers: [tqconstants.HeaderKey, string][] = [
                        [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                        [tqconstants.HeaderKey.ACCEPT, tqconstants.HeaderValue.ACCEPT_JSON],
                    ];
                    req.headers = headers;
                    req.method = tqconstants.RequestMethod.GET;

                    const region: string = await TQUtils.getAppropriateRegion();
                    req.url = req.url.concat("?" + tqconstants.ServerQueryParams.LOCALE_KEY + region);
                }
                break;

            case tqtypes.TaskType.BYOF_UPLOAD_URL:
                {
                    const context = tqContext as tqtypes.GenericContext<tqtypes.UploadContext>;
                    this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.BYOF_STAGE) : (req.url = tqconstants.ServerUrl.BYOF_PROD);
                    req.url += tqconstants.ServerAPI.BYOF_UPLOAD_HANDSHAKE;

                    const headers: [tqconstants.HeaderKey, string][] = [
                        [tqconstants.HeaderKey.ACCEPT, tqconstants.HeaderValue.ALL],
                        [tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId],
                        [tqconstants.HeaderKey.AUTHORIZARION, tqconstants.HeaderValue.BEARER + this._initParams.authParams.accessToken],
                    ];
                    req.headers = headers;
                    req.method = tqconstants.RequestMethod.GET;
                    const sha256sum = await TQUtils.getHash(HashAlgo.SHA256, new Blob([context.root.file]));
                    req.url = req.url.concat("?" + tqconstants.ServerQueryParams.CHECKSUM_KEY + sha256sum);
                }
                break;

            case tqtypes.TaskType.COLLAB_AUTH_API:
                {
                    this._initParams.authParams.isStaging ? (req.url = tqconstants.ServerUrl.TYPEKIT_STAGE) : (req.url = tqconstants.ServerUrl.TYPEKIT_PROD);
                    req.url += tqconstants.ServerAPI.COLLABORATION_FONT_AUTH_URL;
                    req.url += "?include_metadata=true";

                    const context = tqContext as tqtypes.GenericContext<tqtypes.CollaborationAuthInternalContext>;
                    const headers: [tqconstants.HeaderKey, string][] = [[tqconstants.HeaderKey.API_KEY, this.initParams.authParams.appId]];

                    if (TQUtils.isValidAccessToken(this._initParams.authParams.accessToken)) {
                        headers.push([tqconstants.HeaderKey.AUTHORIZARION, "Bearer " + this._initParams.authParams.accessToken]);
                    } else {
                        if (context.root.fontAccessToken != "") {
                            headers.push([tqconstants.HeaderKey.COLLABORATION_AUTHORIZATION, context.root.fontAccessToken]);
                        } else {
                            headers.push([tqconstants.HeaderKey.COLLABORATION_AUTHORIZATION, this._initParams.authParams.accessToken]);
                        }
                    }

                    if (this._initParams.authParams.isStaging && this._initParams.logLevel == TQLogLevel.DEBUG && TQUtils.isValidAccessToken(this._initParams.authParams.accessToken) == false) {
                        //Only supported in pre-prod environments, otherwise ignored. Must be set to "true".)
                        headers.push([tqconstants.HeaderKey.BYPASS_AUTHORIZARION, "true"]);
                    }
                    req.headers = headers;
                    const reqBody = {
                        font_ids: [""],
                        ps_names: [""],
                        document_id: context.root.documentId,
                    };
                    reqBody["font_ids"] = context.root.fontIDs;
                    reqBody["ps_names"] = context.root.psNames;
                    req.body = JSON.stringify(reqBody);
                    req.method = tqconstants.RequestMethod.POST;
                }
                break;

            default:
                // code block
                return true;
        }
        try {
            const connector: TQHttpConnector = new TQHttpConnector(req);
            return connector
                .send()
                .then(response => {
                    return this._processResponse(req, response, tqContext);
                })
                .catch(error => {
                    TQInitParams.Logger.logMessage(TQLogLevel.DEBUG, error);
                    const response: TQHttpResponse = new TQHttpResponse(req.taskType);
                    response.status = tqconstants.ResponseStatus.REQUEST_TIMEOUT;
                    return this._processResponse(req, response, tqContext);
                });
        } catch (error) {
            // ...
            console.log("Request faced an Unfortunate exception");
        }
    }
}
